import { useState, useEffect, useRef } from 'react';
import { SimplePdfViewer } from './simple-pdf-viewer';
import { SignatureCanvas } from './signature-canvas';
import InspectionCamera from './InspectionCamera';
import {
  Camera,
  Upload,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  X,
  Award,
  Calendar,
  Check,
  ChevronDown,
  Clock,
  Globe,
  Phone,
  Shield,
  Star,
  User,
  Building,
  DollarSign,
  AlertCircle,
  Badge,
  Briefcase,
  Flag,
  IdCard,
} from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { AnswerOptionButton } from './answer-option-button';
import { formatDate, showToast } from '../utils/helpers';
import { useTranslation } from 'react-i18next';
import VisitSummaryCard, {
  VisitSummaryData,
} from './inspection-questionnaire/summary/VisitSummaryCard';
import { toast } from 'sonner';
import { PauseCircle } from '@phosphor-icons/react';
import { error } from 'console';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

interface Question {
  id: number;
  question_en: string;
  question_ar: string;
  is_mandatory: boolean;
  category_id: number;
  answer_type: number;
  answer_type_en: string; // Added answer_type_en
  answer_type_ar: string; // Added answer_type_ar
  multiple_choices_en: string;
  multiple_choices_ar: string;
  display_order: number;
  is_active: boolean;
  fines?: [];
  parent_question_id?: number;
  trigger_answer_en?: string; // Added trigger_answer_en
  trigger_answer_ar?: string; // Added trigger_answer_ar
  children?: Question[]; // Added children array for hierarchical structure
  violation_types?: number[]; // Added for violation handling
  is_violation?: boolean; // Added for new violation handling
  fine_type_id?: number; // Added for fine handling
  fine_amount?: number; // Added for fine handling
}

// Interface for violation type reference
interface ViolationType {
  id: number;
  display_name_en: string;
  display_name_ar: string;
}

// Interface for violation reason
interface ViolationReason {
  id: number;
  display_name_en: string;
  display_name_ar: string;
  fine_type_id?: number;
  violation_type_id: number;
}

// Interface for fine information
interface FineInfo {
  fine_type?: string;
  fine_amount?: number;
  total_fine?: number;
}

interface Category {
  id: number;
  display_name_en: string;
  display_name_ar: string;
  inspection_type_id: number;
}

interface QuestionnaireProps {
  onClose: () => void;
  onSubmit: (data: any) => void;
  inspectionTypeId?: number;
  taskDetails?: any;
  language?: string;
  quickMode?: boolean;
}

const BASE_URL = 'https://services-sdp-dev.mohre.gov.ae';

export function InspectionQuestionnaire({
  onClose,
  onSubmit,
  inspectionTypeId = 1,
  taskDetails,
  language = 'en',
  quickMode = false,
}: QuestionnaireProps) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [comments, setComments] = useState<Record<number, string>>(
    {}
  );
  const [showCamera, setShowCamera] = useState(false);
  const [showViolatedWorkerCamera, setShowViolatedWorkerCamera] = useState(false);
  const [photoEvidence, setPhotoEvidence] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showEidDialog, setShowEidDialog] = useState<boolean>(false);
  const [eidNumber, setEidNumber] = useState<string>('');
  const [eidError, setEidError] = useState<string>('');
  // Removed WPS and Work Injury related state variables

  // Removed pagination and related state variables
  // Add state to track viewed questions
  const [viewedQuestions, setViewedQuestions] = useState<Set<number>>(
    new Set()
  );
  const [photos, setPhotos] = useState<Record<number, string[]>>({});
  const [showSummary, setShowSummary] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Add state for success modal
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);
  // Add state variables for visit summary and signature
  const [visitSummary, setVisitSummary] = useState('');
  const [inspectorComment, setInspectorComment] = useState('');
  const [visitSummaryData, setVisitSummaryData] =
    useState<VisitSummaryData>();
  const [pdfUrl, setPdfUrl] = useState('');
  const [signature, setSignature] = useState('');
  // Added state for tracking question hierarchy
  const [questionHierarchy, setQuestionHierarchy] = useState<
    Question[]
  >([]);
  const [loadingChildQuestions, setLoadingChildQuestions] =
    useState(false);

  // State to store the updated task details
  const [updatedTaskDetails, setUpdatedTaskDetails] =
    useState<any>(null);

  const [viewReportData, setViewReportData] = useState<any>(null);

  // Worker violation check states
  const [workerViolationData, setWorkerViolationData] = useState<any>(null);
  const [showWorkerViolationModal, setShowWorkerViolationModal] = useState(false);
  const [submittingViolation, setSubmittingViolation] = useState(false);

  // Timer state
  const [secondsElapsed, setSecondsElapsed] = useState<number>(0);
  const [showPauseConfirmation, setShowPauseConfirmation] = useState<boolean>(false);
  const timerIntervalRef = useRef<number | null>(null);
  const [pauseLoading, setPauseLoading] = useState<boolean>(false);

  // New states for violation handling
  const [violationQuestions, setViolationQuestions] = useState<
    Question[]
  >([]);
  const [showViolationSelection, setShowViolationSelection] =
    useState(false);
  const [violationReasons, setViolationReasons] = useState<
    Record<number, ViolationReason>
  >({});
  const [selectedViolationReasons, setSelectedViolationReasons] =
    useState<Record<number, number[]>>({});
  const [violationTypesReference, setViolationTypesReference] =
    useState<Record<number, ViolationType>>({});
  const [showFineReview, setShowFineReview] = useState(false);
  const [fineInfo, setFineInfo] = useState<FineInfo | null>(null);
  const [loadingViolationReasons, setLoadingViolationReasons] =
    useState(false);

  // State to store category reference data
  const [categoryReference, setCategoryReference] = useState<
    Record<
      number,
      {
        display_name_en: string;
        display_name_ar: string;
        inspection_type_id: number;
      }
    >
  >({});

  // Fetch category reference data
  useEffect(() => {
    const fetchCategoryReference = async () => {
      try {
        console.log('Fetching category reference data');

        const url = `${BASE_URL}/sdp/api/v1/question_category_reference?pageSize=5000`;

        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.user?.access_token}`,
          },
        });

        if (!response.ok) {
          throw new Error(
            `API responded with status: ${response.status}`
          );
        }

        const data = await response.json();
        // console.log('Category reference API response:', data);

        if (data && data.success && data.data) {
          // Create a lookup map for efficient access
          const categoryMap: Record<
            number,
            {
              display_name_en: string;
              display_name_ar: string;
              inspection_type_id: number;
            }
          > = {};

          data.data.forEach((item: any) => {
            categoryMap[item.id] = {
              display_name_en:
                item.display_name_en || `Category ${item.id}`,
              display_name_ar:
                item.display_name_ar || `الفئة ${item.id}`,
              inspection_type_id: item.inspection_type_id || 0,
            };
          });

          setCategoryReference(categoryMap);
          // console.log('Category reference data cached:', categoryMap);
        }
      } catch (error) {
        console.error(
          'Error fetching category reference data:',
          error
        );
      }
    };

    fetchCategoryReference();
  }, [auth?.user?.access_token]);

  // Fetch violation types reference data
  useEffect(() => {
    const fetchViolationTypesReference = async () => {
      try {
        console.log('Fetching violation types reference data');
        const url = `${BASE_URL}/sdp/api/v1/ViolationTypeReference`;

        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.user?.access_token}`,
          },
        });

        if (!response.ok) {
          throw new Error(
            `API responded with status: ${response.status}`
          );
        }

        const data = await response.json();
        console.log('Violation types reference API response:', data);

        if (data && data.success && data.data) {
          const violationTypesMap: Record<number, ViolationType> = {};

          data.data.forEach((item: any) => {
            violationTypesMap[item.id] = {
              id: item.id,
              display_name_en:
                item.display_name_en || `Violation Type ${item.id}`,
              display_name_ar:
                item.display_name_ar || `نوع المخالفة ${item.id}`,
            };
          });

          setViolationTypesReference(violationTypesMap);
          console.log(
            'Violation types reference data cached:',
            violationTypesMap
          );
        }
      } catch (error) {
        console.error(
          'Error fetching violation types reference data:',
          error
        );
      }
    };

    fetchViolationTypesReference();
  }, [auth?.user?.access_token]);

  // Function to fetch violation reasons
  const fetchViolationReasons = async () => {
    try {
      setLoadingViolationReasons(true);
      console.log('Fetching violation reasons');

      const url = `${BASE_URL}/sdp/api/v1/CatchViolationReasonReference`;

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth?.user?.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `API responded with status: ${response.status}`
        );
      }

      const data = await response.json();
      console.log('Violation reasons API response:', data);

      if (data && data.success && data.data) {
        // Store reasons as a hash map by id with display_name_en/display_name_ar
        const reasonsMap: Record<number, ViolationReason> = {};

        data.data.forEach((item: any) => {
          reasonsMap[item.id] = {
            id: item.id,
            display_name_en:
              item.display_name_en || `Reason ${item.id}`,
            display_name_ar: item.display_name_ar || `سبب ${item.id}`,
            fine_type_id: item.fine_type_id,
            violation_type_id: item.violation_type_id,
          };
        });

        setViolationReasons(reasonsMap);
        console.log('Violation reasons mapped by id:', reasonsMap);
      }
    } catch (error) {
      console.error('Error fetching violation reasons:', error);
      showToast('error', 'Failed to load violation reasons');
    } finally {
      setLoadingViolationReasons(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  // Get gender from gender_id
  const getGender = (genderId: number) => {
    return genderId === 1 ? "Male" : genderId === 2 ? "Female" : "Other"
  }

  // Calculate age
  const calculateAge = (birthDateString: string) => {
    if (!birthDateString) return "N/A"
    const birthDate = new Date(birthDateString)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  // Removed formatYMonth and formatIncidentDate functions

  // Mask card number to show only first two digits
  const maskCardNumber = (cardNumber: string | null | undefined) => {
    if (!cardNumber) return '-';
    if (cardNumber.length < 3) return cardNumber;
    return (
      cardNumber.substring(0, 2) + 'X'.repeat(cardNumber.length - 2)
    );
  };

  // Mask EID number to show only last 4 digits
  const maskEidNumber = (eidNumber: string | null | undefined) => {
    if (!eidNumber) return '-';
    if (eidNumber.length <= 4) return eidNumber;
    return 'X'.repeat(eidNumber.length - 4) + eidNumber.slice(-4);
  };


  // Fetch task details and then questionnaire data
  useEffect(() => {
    const fetchTaskDetails = async () => {
      try {
        setLoading(true);

        // Check if we have a task_id to fetch details for
        if (!taskDetails?.id) {
          console.log(
            'No task ID available, using default inspection type'
          );
          fetchQuestionnaireData(inspectionTypeId);
          return;
        }

        console.log(
          `Fetching task details for task ID: ${taskDetails.id}`
        );

        console.log(
          `Fetching tid from localstorage: ${localStorage.getItem(
            'tid'
          )}`
        );

        const tid = localStorage.getItem('tid')
          ? localStorage.getItem('tid')
          : taskDetails.id;

        // Fetch task details
        const taskUrl = `${BASE_URL}/jsonapi/transform/task_list?id=${tid}`;

        const taskResponse = await fetch(taskUrl, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.user?.access_token}`,
          },
        });

        if (!taskResponse.ok) {
          throw new Error(
            `Task details API responded with status: ${taskResponse.status}`
          );
        }

        const taskData = await taskResponse.json();
        console.log('Task details API response:', taskData);

        if (taskData && taskData.data && taskData.data.length > 0) {
          const updatedTask = taskData.data[0];
          setUpdatedTaskDetails(updatedTask);

          // Get the inspection_type_id from the updated task details
          const typeId = updatedTask.params?.inspection_type_id
            ? parseInt(updatedTask.params.inspection_type_id)
            : inspectionTypeId;

          console.log(
            'Using inspection_type_id from task details:',
            typeId
          );

          // Fetch questionnaire data with the updated inspection_type_id
          fetchQuestionnaireData(typeId);
        } else {
          console.log(
            'No task details found, using default inspection type'
          );
          fetchQuestionnaireData(inspectionTypeId);
        }
      } catch (error) {
        console.error('Error fetching task details:', error);
        showToast('error', 'Failed to load task details');

        // Fall back to using the original inspection type ID
        fetchQuestionnaireData(inspectionTypeId);
      }
    };

    const fetchQuestionnaireData = async (typeId: number) => {
      try {
        console.log(
          'Loading questionnaire data for inspection type:',
          typeId
        );

        // Construct the API URL with the inspection_type_id and only fetch root questions
        const conditionParam = {
          operation: 'AND',
          children: [
            {
              operation: 'OR',
              rules: [
                {
                  field: 'parent_question_id',
                  operator: 'eq',
                  value: 0,
                },
                {
                  field: 'parent_question_id',
                  operator: 'null',
                },
              ],
            },
            {
              operation: 'AND',
              rules: [
                {
                  field: 'inspection_type_id',
                  operator: 'eq',
                  value: typeId,
                },
              ],
            },
          ],
        };

        const url = `${BASE_URL}/sdp/rules/api/v2/question/get_questions?page=1&pageSize=300&condition=${encodeURIComponent(
          JSON.stringify(conditionParam)
        )}`;

        console.log('Fetching root questionnaire data from:', url);

        // Make the API call with authorization header
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.user?.access_token}`,
          },
        });

        if (!response.ok) {
          throw new Error(
            `API responded with status: ${response.status}`
          );
        }

        const data = await response.json();
        console.log('Root questionnaire API response:', data);

        // Check if we have data.data or data.items based on API response structure
        const responseData = data.data || data.items || [];
        // Extract categories and questions from the response
        if (responseData.length > 0) {
          // Process categories
          const uniqueCategories = new Map<number, Category>();

          // Extract unique categories from questions
          responseData.forEach((item: any) => {
            const categoryId =
              item.category_id || item.question_category_id;
            if (categoryId && !uniqueCategories.has(categoryId)) {
              uniqueCategories.set(categoryId, {
                id: categoryId,
                display_name_en:
                  item.category_name_en ||
                  item.question_category_en ||
                  `Category ${categoryId}`,
                display_name_ar:
                  item.category_name_ar ||
                  item.question_category_ar ||
                  `الفئة ${categoryId}`,
                inspection_type_id: typeId,
              });
            }
          });

          setCategories(Array.from(uniqueCategories.values()));

          // Process questions
          const mappedQuestions: Question[] = responseData.map(
            (item: any) => ({
              id: item.question_id,
              question_en:
                item.question_en || 'No question available',
              question_ar: item.question_ar || 'لا يوجد سؤال متاح',
              is_mandatory: item.is_mandatory,
              category_id:
                item.category_id || item.question_category_id || 0,
              answer_type: item.answer_type,
              answer_type_en:
                item.answer_type_en || 'Multiple Choice',
              answer_type_ar: item.answer_type_ar || 'اختيار متعدد',
              multiple_choices_en:
                item.multiple_choices_en || 'Yes-No-N/A',
              multiple_choices_ar:
                item.multiple_choices_ar || 'نعم-لا-غير متوفر',
              display_order: item.display_order || 0,
              is_active:
                item.is_active === true ||
                item.is_active === 'true' ||
                item.is_active === 1,
              parent_question_id: item.parent_question_id
                ? parseInt(item.parent_question_id)
                : undefined,
              trigger_answer_en: item.trigger_answer_en || null,
              trigger_answer_ar: item.trigger_answer_ar || null,
              children: [],
            })
          );

          // Filter active questions
          const activeQuestions = mappedQuestions.filter(
            (q) => q.is_active
          );

          // First, group questions by category
          const questionsByCategory = activeQuestions.reduce(
            (acc, question) => {
              const categoryId = question.category_id;
              if (!acc[categoryId]) {
                acc[categoryId] = [];
              }
              acc[categoryId].push(question);
              return acc;
            },
            {} as Record<number, Question[]>
          );

          // Sort each category's questions by display_order
          Object.keys(questionsByCategory).forEach((categoryId) => {
            const numericCategoryId = parseInt(categoryId);

            // First sort by display_order
            questionsByCategory[numericCategoryId].sort(
              (a, b) => a.display_order - b.display_order
            );

            // Then ensure child questions are placed after their parent questions
            const sortedQuestions: Question[] = [];
            const processedIds = new Set<number>();

            // Helper function to add a question and its children recursively
            const addQuestionAndChildren = (question: Question) => {
              if (processedIds.has(question.id)) return;

              sortedQuestions.push(question);
              processedIds.add(question.id);

              // Find and add all direct children
              const children = questionsByCategory[
                numericCategoryId
              ].filter(
                (q) =>
                  q.parent_question_id === question.id &&
                  !processedIds.has(q.id)
              );

              children.forEach((child) =>
                addQuestionAndChildren(child)
              );
            };

            // Process all root questions (those without parents or with parents not in this category)
            questionsByCategory[numericCategoryId].forEach(
              (question) => {
                if (
                  !question.parent_question_id ||
                  !questionsByCategory[numericCategoryId].some(
                    (q) => q.id === question.parent_question_id
                  )
                ) {
                  addQuestionAndChildren(question);
                }
              }
            );

            // Handle any remaining questions that weren't processed
            questionsByCategory[numericCategoryId].forEach(
              (question) => {
                if (!processedIds.has(question.id)) {
                  addQuestionAndChildren(question);
                }
              }
            );

            questionsByCategory[numericCategoryId] = sortedQuestions;
          });

          // Flatten the grouped questions back into a single array, maintaining category order
          const sortedQuestions = Object.keys(questionsByCategory)
            .sort((a, b) => parseInt(a) - parseInt(b))
            .flatMap(
              (categoryId) =>
                questionsByCategory[parseInt(categoryId)]
            );

          console.log(
            'Questions sorted by category and parent-child relationship:',
            sortedQuestions.map((q) => ({
              id: q.id,
              category_id: q.category_id,
              parent_id: q.parent_question_id,
              display_order: q.display_order,
            }))
          );

          // Use the sorted questions instead of just the active questions
          setQuestions(sortedQuestions);
          setQuestionHierarchy(sortedQuestions);
        } else {
          // If no data is returned, set empty arrays
          setCategories([]);
          setQuestions([]);
        }
      } catch (error) {
        console.error('Error loading questionnaire data:', error);
        showToast('error', 'Failed to load questionnaire data');

        // Set empty data on error
        setCategories([]);
        setQuestions([]);
      } finally {
        setLoading(false);
      }
    };

    // Start the process by fetching task details
    fetchTaskDetails();
  }, [inspectionTypeId, taskDetails, auth?.user?.access_token]);

  // showToastLocal function has been removed - using showToast directly from helpers.ts

  // Validate Emirates ID format (15 digits)
  const validateEid = (eid: string): boolean => {
    const eidRegex = /^\d{15}$/;
    return eidRegex.test(eid);
  };

  // Handle EID form submission
  const handleEidSubmit = () => {
    if (!validateEid(eidNumber)) {
      setEidError('Emirates ID must be 15 digits');
      return;
    }

    setShowEidDialog(false);
    setEidError('');

    // Call checkWorkerViolation with the validated EID
    checkWorkerViolation(eidNumber);
  };

  // Function to submit violation form
  const handleSubmitViolationForm = async () => {
    if (!workerViolationData) return;

    setSubmittingViolation(true);

    try {
      // Dummy API call placeholder
      const apiUrl = `${BASE_URL}/sdp/rules/api/v2/task/actions/create_child_task`;
      const payload = {
        parent_task_id: updatedTaskDetails?.id, // Use updatedTaskDetails or fallback
        other_establishment_id: workerViolationData.establishment_id,
        violated_worker_id: workerViolationData.person_id,
        summary: 'Found violating worker',
        incident_description: 'Found violating worker',
      };

      console.log('Submitting violation form with payload:', payload);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth?.user?.access_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})); // Try to parse error, default to empty object
        console.error('API Error Response:', errorData);
        throw new Error(
          `API call failed with status ${response.status}: ${
            errorData?.message || response.statusText
          }`
        );
      }

      const responseData = await response.json();
      console.log('Violation submission response:', responseData);

      // Use toast for success message
      toast.success(language === 'en'
        ? 'Violation submitted successfully.'
        : 'تم تقديم المخالفة بنجاح.');

      // Close the modal and clear data
      setShowWorkerViolationModal(false);
      setWorkerViolationData(null); // Clear the data after successful submission
    } catch (error) {
      console.error('Error submitting violation form:', error);
      toast.error(language === 'en'
        ? 'Failed to submit violation form'
        : 'فشل في تقديم نموذج المخالفة');
    } finally {
      setSubmittingViolation(false);
    }
  };

  // Function to check worker violation using Emirates ID
  const checkWorkerViolation = async (eid: string) => {
    console.log('Checking worker violation for EID:', eid);
    const conditionParam = {
      rules: [
        {
          operator: 'eq',
          field: 'eid_number',
          value: eid,
        },
      ],
    };
      const params = new URLSearchParams({
        condition: JSON.stringify(conditionParam),
      });
    try {
      const response = await fetch(`${BASE_URL}/sdp/api/v1/vw_WorkPermitDetails?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth?.user?.access_token}`,
        }
      });

      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const data = await response.json();
      if (data.data && data.data.length) {
        setWorkerViolationData(data.data[0]);
        setShowWorkerViolationModal(true);
      } else {
        toast.error('Worker not found!');
      }
      console.log('Worker violation check response:', data);

    } catch (error) {
      console.error('Error checking worker violation:', error);
      showToast('error', 'Failed to check worker violation');
      toast.error('Failed to check worker violation');
      setShowWorkerViolationModal(false);
    }
  };

  // Function to fetch child questions based on parent question and selected answer
  const fetchChildQuestions = async (
    parentId: number,
    selectedAnswer: string
  ) => {
    try {
      setLoadingChildQuestions(true);
      console.log(
        `Fetching child questions for parent ID: ${parentId} with answer: ${selectedAnswer}`
      );

      const conditionParam = {
        operation: 'AND',
        children: [
          {
            operation: 'AND',
            rules: [
              {
                field: 'parent_question_id',
                operator: 'eq',
                value: parentId,
              },
              {
                field: 'trigger_answer_en',
                operator: 'eq',
                value: selectedAnswer,
              },
              {
                field: 'inspection_type_id',
                operator: 'eq',
                value:
                  updatedTaskDetails?.params?.inspection_type_id ||
                  inspectionTypeId,
              },
            ],
          },
        ],
      };

      const url = `${BASE_URL}/sdp/rules/api/v2/question/get_questions?page=1&pageSize=300&condition=${encodeURIComponent(
        JSON.stringify(conditionParam)
      )}`;

      console.log('Fetching child questions from:', url);

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth?.user?.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `API responded with status: ${response.status}`
        );
      }

      const data = await response.json();
      console.log('Child questions API response:', data);

      // Check if we have data.data or data.items based on API response structure
      const responseData = data.data || data.items || [];

      if (responseData.length > 0) {
        // Map the child questions
        const childQuestions: Question[] = responseData.map(
          (item: any) => ({
            id: item.question_id,
            question_en: item.question_en || 'No question available',
            question_ar: item.question_ar || 'لا يوجد سؤال متاح',
            is_mandatory:
              item.is_mandatory === true ||
              item.is_mandatory === 'true' ||
              item.is_mandatory === 1,
            category_id:
              item.category_id || item.question_category_id || 0,
            answer_type: item.answer_type || 0,
            answer_type_en: item.answer_type_en || 'Multiple Choice',
            answer_type_ar: item.answer_type_ar || 'اختيار متعدد',
            multiple_choices_en:
              item.multiple_choices_en || 'Yes-No-N/A',
            multiple_choices_ar:
              item.multiple_choices_ar || 'نعم-لا-غير متوفر',
            display_order: item.display_order || 0,
            is_active:
              item.is_active === true ||
              item.is_active === 'true' ||
              item.is_active === 1,
            parent_question_id: item.parent_question_id
              ? parseInt(item.parent_question_id)
              : undefined,
            trigger_answer_en: item.trigger_answer_en || null,
            trigger_answer_ar: item.trigger_answer_ar || null,
            children: [],
          })
        );

        return childQuestions;
      }

      return [];
    } catch (error) {
      console.error('Error fetching child questions:', error);
      return [];
    } finally {
      setLoadingChildQuestions(false);
    }
  };

  // Function to update the question hierarchy with new child questions
  const updateQuestionHierarchy = (
    questions: Question[],
    parentId: number,
    childQuestions: Question[]
  ): Question[] => {
    return questions.map((question) => {
      if (question.id === parentId) {
        // Found the parent, add children
        return {
          ...question,
          children: childQuestions,
        };
      } else if (question.children && question.children.length > 0) {
        // Check in children recursively
        return {
          ...question,
          children: updateQuestionHierarchy(
            question.children,
            parentId,
            childQuestions
          ),
        };
      }
      return question;
    });
  };

  // Function to flatten the hierarchical questions into a single array
  const flattenQuestions = (questions: Question[]): Question[] => {
    let result: Question[] = [];

    questions.forEach((question) => {
      result.push(question);
      if (question.children && question.children.length > 0) {
        result = [...result, ...flattenQuestions(question.children)];
      }
    });

    return result;
  };

  // Modified handleAnswer to fetch child questions when appropriate
  const handleAnswer = async (questionId: number, answer: string) => {
    // Update the answer in state
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: answer,
    }));

    // Find the question that was answered
    const answeredQuestion = questions.find(
      (q) => q.id === questionId
    );

    if (answeredQuestion) {
      // If this is a List type question, we might need to fetch child questions
      if (answer) {
        // Clear any existing child questions for this parent
        // This is important if the user changes their answer
        const updatedHierarchy = questionHierarchy.map((q) => {
          if (q.id === questionId) {
            return { ...q, children: [] };
          }
          return q;
        });

        setQuestionHierarchy(updatedHierarchy);

        // Fetch child questions based on the selected answer
        const childQuestions = await fetchChildQuestions(
          questionId,
          answer
        );

        if (childQuestions.length > 0) {
          // Update the hierarchy with the new child questions
          const newHierarchy = updateQuestionHierarchy(
            updatedHierarchy,
            questionId,
            childQuestions
          );

          setQuestionHierarchy(newHierarchy);

          // Flatten the hierarchy to update the questions array
          const flattenedQuestions = flattenQuestions(newHierarchy);
          setQuestions(flattenedQuestions);
        }
      }
    }
  };

  const handleCommentChange = (
    questionId: number,
    comment: string
  ) => {
    setComments({
      ...comments,
      [questionId]: comment,
    });
  };

  // Function to navigate to a specific question
  const navigateToQuestion = (index: number) => {
    setCurrentQuestionIndex(index);
  };

  const goToNextQuestion = async () => {
    const currentQuestion = questions[currentQuestionIndex];

    // Check if current question is mandatory and not answered
    if (
      currentQuestion?.is_mandatory &&
      !answers[currentQuestion.id]
    ) {
      showToast(
        'error',
        'Please answer this mandatory question before proceeding'
      );
      return;
    }

    // If we're at a question with children that need to be loaded
    // based on the answer, make sure they're loaded before proceeding
    if (
      currentQuestion?.answer_type_en === 'List' &&
      answers[currentQuestion.id] &&
      !currentQuestion.children?.length
    ) {
      // Check if we need to wait for child questions to load
      if (loadingChildQuestions) {
        showToast(
          'error',
          'Loading follow-up questions, please wait...'
        );
        return;
      }
    }

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Show summary before submission
      handleSubmit();
    }
  };

  const goToPreviousQuestion = () => {
    if (showViolationSelection) {
      setShowViolationSelection(false);
    } else if (currentQuestionIndex > 0) {
      setShowSummary(false);
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } else {
      onClose();
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // Check if all mandatory questions are answered
      const unansweredMandatory = questions.filter(
        (q) => q.is_mandatory && !answers[q.id]
      );

      if (unansweredMandatory.length > 0) {
        showToast(
          'error',
          `${unansweredMandatory.length} mandatory questions are unanswered`
        );
        setShowSummary(false);
        setCurrentQuestionIndex(
          questions.findIndex(
            (q) => q.id === unansweredMandatory[0].id
          )
        );
        setSubmitting(false);
        return;
      }

      // Count compliance and non-compliance answers
      let complianceCount = 0;
      let nonComplianceCount = 0;

      // Use the updated task details if available, otherwise fall back to the original
      const taskId =
        updatedTaskDetails?.task_id || taskDetails?.id || '';
      const establishmentId =
        updatedTaskDetails?.establishment_id ||
        taskDetails?.establishment_id ||
        '';
      const typeId = updatedTaskDetails?.params?.inspection_type_id
        ? parseInt(updatedTaskDetails.params.inspection_type_id)
        : inspectionTypeId;

      // Prepare submission data
      const submissionData = {
        task_id: taskId,
        inspection_type_id: typeId,
        answers: Object.entries(answers).map(
          ([questionId, answer]) => ({
            question_id: parseInt(questionId),
            answer,
            comment: comments[parseInt(questionId)] || '',
            photos: photos[parseInt(questionId)] || [],
            ...(selectedViolationReasons[parseInt(questionId)] && {
              violation_reason:
                selectedViolationReasons[parseInt(questionId)],
            }),
          })
        ),
      };

      // Prepare report data for API
      const reportData_ = {
        task_id: taskId,
        exceptions: '',
        compliance_count: complianceCount.toString(),
        non_compliance_count: nonComplianceCount.toString(),
        details: JSON.stringify(submissionData),
      };

      // Updated reportData to use submissionData.answers directly as requested
      const reportData = {
        status: 'In Progress',
        task_id: taskId,
        establishment_id: establishmentId,
        details: submissionData.answers,
      };

      console.log('Submitting report data:', reportData);

      // Submit the report to the API
      try {
        const domain = BASE_URL; // Using the BASE_URL defined at the top of the file
        const response = await fetch(
          `${domain}/sdp/rules/api/v2/task/update_task_report`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth?.user?.access_token}`,
            },
            body: JSON.stringify(reportData),
          }
        );

        if (!response.ok) {
          throw new Error(
            `API responded with status: ${response.status}`
          );
        }

        const result = await response.json();
        console.log('API response:', result);
        setViewReportData(result.data);

        // Check if there are violations in the response
        if (result.data && result.data.details) {
          result.data.details = JSON.parse(result.data.details);
          // Extract questions with violations - checking for is_violation: true
          const questionsWithViolations = result.data.details.filter(
            (item: any) => item.is_violation === true
          );

          if (questionsWithViolations.length > 0) {
            // Map the violation questions to our Question interface
            const violationQs = questionsWithViolations.map(
              (item: any) => {
                // Find the original question to get its text and other properties
                const originalQuestion = questions.find(
                  (q) => q.id === item.question_id
                );

                return {
                  ...originalQuestion,
                  id: item.question_id,
                  is_violation: true,
                  fine_type_id: item.fine_type_id,
                  fine_amount: item.fine_amount,
                  fine_types: item.fine_types,
                  fines: item.fines,
                  violation_reason: item.violation_reason,
                } as Question;
              }
            );

            setViolationQuestions(violationQs);
            if (Object.keys(violationReasons).length === 0) {
              await fetchViolationReasons();
            }
          }

          if (
            questionsWithViolations.length > 0 &&
            !showViolationSelection
          ) {
            // Show the violation selection UI
            setShowViolationSelection(true);
            setShowSummary(false);
            setSubmitting(false);
            return;
          }

          // Check if there are fines in the response
          if (false && !showFineReview) {
            const violationQs = questionsWithViolations.map(
              (item: any) => {
                // Find the original question to get its text and other properties
                const originalQuestion = questions.find(
                  (q) => q.id === item.question_id
                );

                return {
                  ...originalQuestion,
                  id: item.question_id,
                  is_violation: true,
                  fine_type_id: item.fine_type_id,
                  fine_amount: item.fine_amount,
                  fine_types: item.fine_types,
                  fines: item.fines,
                  violation_reason: item.violation_reason,
                } as Question;
              }
            );

            setViolationQuestions(violationQs);
            setFineInfo({
              total_fine: result.data.total_fine,
            });

            // If we have violations, show the violation selection first

            // Otherwise, show the fine review directly
            setShowFineReview(true);
            setShowViolationSelection(false);
            setShowSummary(false);
            setSubmitting(false);
            return;
          }
          if (!showSummary) {
            setShowFineReview(false);
            setShowViolationSelection(false);
            setShowSummary(true);
            setSubmitting(false);
            return;
          }
        }

        if (result.data.status) {
          showToast('success', result.data.message);
        } else {
          showToast('error', result.data.message);
        }
        setSubmitting(false);
        onSubmit(submissionData);

        // Navigate to the Inspections page after successful submission
        navigate('/plan');
      } catch (apiError) {
        console.error('Error calling submit-report API:', apiError);
        showToast(
          'error',
          'Failed to submit report to API, but local data is saved'
        );

        // Still call onSubmit to update local state
        setSubmitting(false);
        onSubmit(submissionData);
      }
    } catch (error) {
      console.error('Error submitting inspection:', error);
      showToast('error', 'Failed to submit inspection');
      setSubmitting(false);
    }
  };

  // Function to handle violation reason selection
  const handleViolationReasonSelect = (
    questionId: number,
    reasonId: number,
    isChecked: boolean
  ) => {
    setSelectedViolationReasons((prev) => {
      const currentReasons = prev[questionId] || [];

      if (isChecked) {
        // Add the reason if it's not already in the array
        return {
          ...prev,
          [questionId]: [...currentReasons, reasonId],
        };
      } else {
        // Remove the reason if it's in the array
        return {
          ...prev,
          [questionId]: currentReasons.filter(
            (id) => id !== reasonId
          ),
        };
      }
    });
  };
  // Function to submit the final report after violation handling
  const handleFinalSubmit = async () => {
    try {
      setSubmitting(true);

      // Use the updated task details if available, otherwise fall back to the original
      const taskId =
        updatedTaskDetails?.task_id || taskDetails?.id || '';
      const establishmentId =
        updatedTaskDetails?.establishment_id ||
        taskDetails?.establishment_id ||
        '';
      const typeId = updatedTaskDetails?.params?.inspection_type_id
        ? parseInt(updatedTaskDetails.params.inspection_type_id)
        : inspectionTypeId;

      // Prepare submission data with violation reasons
      const submissionData = {
        task_id: taskId,
        inspection_type_id: typeId,
        visit_summary: visitSummary + ' ' + inspectorComment,
        signature: signature,
        answers: Object.entries(answers).map(
          ([questionId, answer]) => ({
            question_id: parseInt(questionId),
            answer,
            comment: comments[parseInt(questionId)] || '',
            photos: photos[parseInt(questionId)] || [],
            ...(selectedViolationReasons[parseInt(questionId)] && {
              violation_reason:
                selectedViolationReasons[parseInt(questionId)],
            }),
          })
        ),
      };

      // Updated reportData to use submissionData.answers directly and include visit_summary
      const reportData = {
        status: 'completed',
        task_id: taskId,
        establishment_id: establishmentId,
        details: submissionData.answers,
        visit_summary: visitSummary + ' ' + inspectorComment, // Added visit_summary to the payload
      };

      console.log('Submitting final report data:', reportData);

      // Submit the final report to the API
      const domain = BASE_URL;
      const response = await fetch(
        `${domain}/sdp/rules/api/v2/task/update_task_report`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.user?.access_token}`,
          },
          body: JSON.stringify(reportData),
        }
      );

      if (!response.ok) {
        throw new Error(
          `API responded with status: ${response.status}`
        );
      }

      const result = await response.json();
      console.log('Final submission API response:', result);

      if (result.data && result.data.status) {
        showToast(
          'success',
          result.data.message || 'Report submitted successfully'
        );
      } else {
        showToast(
          'success',
          result.data?.message || 'Report submitted'
        );
      }

      // Calculate points earned based on completed questions
      // Each completed question is worth 1 point, with a bonus for violations detected
      const completedQuestions = Object.keys(answers).length;
      const violationsDetected = Object.keys(
        selectedViolationReasons
      ).length;
      const calculatedPoints =
        completedQuestions + violationsDetected * 2;

      // Set points earned and show success modal
      setPointsEarned(calculatedPoints);
      setShowSuccessModal(true);

      setSubmitting(false);
      onSubmit(submissionData);

      // Navigation to /inspections will now happen after the user clicks "Continue" in the success modal
    } catch (error) {
      console.error('Error submitting final report:', error);
      showToast('error', 'Failed to submit final report');
      setSubmitting(false);
    }
  };

  const viewReportPdf = async () => {
    setSubmitting(true);
    try {
      viewReportData.visit_summary = visitSummary;
      const date = new Date();
      const cryptoRandomNumber = (max) => {
        const maxUint32 = 0xFFFFFFFF;
        const limit = maxUint32 - (maxUint32 % max);
        const randArray = new Uint32Array(1);
        while (true) {
          crypto.getRandomValues(randArray);
          if (randArray[0] < limit) {
            return randArray[0] % max;
          }
        }
      };

      const formatted = `${date
        .toISOString()
        .slice(0, -1)}${cryptoRandomNumber(1000)
        .toString()
        .padStart(3, '0')}`;
      viewReportData.end_date = formatted;
      viewReportData.compliant_count = (
        viewReportData.compliant_count || 0
      ).toString();
      viewReportData.non_compliant_count = (
        viewReportData.non_compliant_count || 0
      ).toString();
      viewReportData.details = JSON.stringify(viewReportData.details);
      const domain = BASE_URL; // Using the BASE_URL defined at the top of the file
      const response = await fetch(
        `${domain}/sdp/rules/api/v2/report/generate-report`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/pdf',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.user?.access_token}`,
          },
          body: JSON.stringify([viewReportData]),
        }
      );

      if (!response.ok) {
        throw new Error(
          `API responded with status: ${response.status}`
        );
      }
      setSubmitting(false);
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl); // Prevent memory leaks
      }
      const blob = await response.blob();

      if (blob.type !== 'application/pdf') {
        console.error('Invalid blob type:', blob.type);
        showToast('error', 'Invalid PDF format received from server');
        return;
      }

      // Create a blob URL that our PDF viewer can use
      const url = URL.createObjectURL(blob);
      console.log('PDF blob URL created successfully');
      setPdfUrl(url);
    } catch (apiError) {
      console.error('Error calling submit-report API:', apiError);
      showToast(
        'error',
        'Failed to submit report to API, but local data is saved'
      );

      // Still call onSubmit to update local state
      setSubmitting(false);
    }
  };

  // Get the current question
  const currentQuestion =
    !showSummary && questions.length > 0
      ? questions[currentQuestionIndex]
      : null;

  // Update viewed questions whenever the current question changes
  useEffect(() => {
    if (currentQuestion) {
      setViewedQuestions((prev) => {
        const newSet = new Set(prev);
        newSet.add(currentQuestion.id);
        return newSet;
      });
    }
  }, [currentQuestion]);

  // Function to generate a detailed visit summary
  const generateVisitSummary = () => {
    // Get task and establishment details
    const task = updatedTaskDetails || taskDetails || {};

    // Extract establishment details
    const establishment = task.establishment || {};
    const establishmentNameEn =
      establishment.name_en ||
      task.establishment_name_en ||
      task.params?.establishment_name ||
      'N/A';
    const establishmentNameAr =
      establishment.name_ar || task.establishment_name_ar || 'N/A';
    const establishmentId =
      task.establishment_id ||
      task.params?.establishment_id ||
      establishment.id ||
      'N/A';

    // Extract location details
    const emirateEn =
      task.emirate_display || task.params?.emirate_display || 'N/A';
    const zoneEn =
      task.zone_display || task.params?.zone_display || 'N/A';
    const latitude = establishment.latitude || 'N/A';
    const longitude = establishment.longitude || 'N/A';

    // Extract task details
    const taskId = task.task_id || task.id || 'N/A';
    const taskTypeEn =
      task.task_type_display || task.params?.task_type || 'N/A';
    const inspectionTypeEn =
      task.params?.inspection_type_display || 'N/A';
    const taskStatus = task.task_status_display || 'N/A';
    const riskScore = task.risk_score || 'N/A';

    // Extract incident details
    const incidentDescription =
      task.params?.incident_description || 'N/A';
    const subject = task.params?.subject || 'N/A';
    const reportedDate = task.params?.reported_date || 'N/A';

    // Extract establishment business details
    const licenseNo = establishment.license_no || 'N/A';
    const licenseExpiryDate = establishment.license_expiry_date
      ? new Date(
          establishment.license_expiry_date
        ).toLocaleDateString()
      : 'N/A';
    const nationalityEn = establishment.nationality_en || 'N/A';
    const establishmentTypeEn =
      establishment.establishment_type_en || 'N/A';

    // Get current date and time
    const now = new Date();
    const dateTimeStr = now.toLocaleString(
      language === 'ar' ? 'ar-AE' : 'en-AE',
      {
        dateStyle: 'full',
        timeStyle: 'medium',
        timeZone: 'Asia/Dubai',
      }
    );

    // Count compliance and non-compliance answers
    let complianceCount = 0;
    let nonComplianceCount = 0;

    // Count answers by type
    Object.values(answers).forEach((answer) => {
      if (answer === 'Yes') complianceCount++;
      if (answer === 'No') nonComplianceCount++;
    });

    // Split dateTimeStr into date and time
    const [datePart, timePart] = dateTimeStr
      .split(',')
      .map((str) => str.trim());

    // Generate VisitSummaryData object with separate date and time
    const vsd = {
      // Date & Time
      date: datePart,
      time: timePart,

      // Task Information
      taskId: taskDetails?.id || 'N/A',
      taskType: taskDetails?.task_type_display || 'N/A',
      taskStatus: taskDetails?.task_status_display || 'N/A',
      riskScore: taskDetails?.risk_score || 'N/A',

      // Incident Information
      incident: {
        subject: taskDetails?.params?.subject || 'N/A',
        date: taskDetails?.params?.reported_date || 'N/A',
        description:
          taskDetails?.params?.incident_description || 'N/A',
        riskFactors: [], // Add risk factors if available
      },

      // Establishment Details
      establishment: {
        name: taskDetails?.establishment_name_en || 'N/A',
        id: taskDetails?.establishment_id || 'N/A',
        licenseNumber:
          taskDetails?.establishment?.license_no || 'N/A',
        licenseExpiry:
          taskDetails?.establishment?.license_expiry_date || 'N/A',
        type:
          taskDetails?.establishment?.establishment_type_en || 'N/A',
        nationality:
          taskDetails?.establishment?.nationality_en || 'N/A',
      },

      // Location Details
      location: {
        emirate: taskDetails?.emirate_display || 'N/A',
        zone: taskDetails?.zone_display || 'N/A',
        coordinates: {
          latitude: Number(taskDetails?.establishment?.latitude) || 0,
          longitude:
            Number(taskDetails?.establishment?.longitude) || 0,
        },
      },

      // Inspection Summary
      inspectionSummary: {
        totalQuestions: questions.length,
        answeredQuestions: Object.keys(answers).length,
        compliantAnswers: Object.values(answers).filter(
          (a) => a === 'Yes'
        ).length,
        nonCompliantAnswers: Object.values(answers).filter(
          (a) => a === 'No'
        ).length,
        naAnswers: Object.values(answers).filter((a) => a === 'N/A')
          .length,
      },

      // Question Details
      questions: questions.map((question) => ({
        id: question.id,
        question: question.question_en,
        answer: answers[question.id] || 'Unanswered',
        isCompliant: answers[question.id] === 'Yes',
      })),
    };

    setVisitSummaryData(vsd);

    // Generate summary text
    let summaryText = '';

    // Add header based on language
    if (language === 'en') {
      summaryText += `INSPECTION VISIT SUMMARY\n`;
      summaryText += `=======================\n\n`;
      summaryText += `Date & Time: ${dateTimeStr}\n\n`;

      // Task Information
      summaryText += `TASK INFORMATION:\n`;
      summaryText += `Task ID: ${taskId}\n`;
      summaryText += `Task Type: ${taskTypeEn}\n`;
      summaryText += `Inspection Type: ${inspectionTypeEn}\n`;
      summaryText += `Task Status: ${taskStatus}\n`;
      summaryText += `Risk Score: ${riskScore}\n\n`;

      // Incident Information (if available)
      if (incidentDescription !== 'N/A' || subject !== 'N/A') {
        summaryText += `INCIDENT INFORMATION:\n`;
        summaryText += `Subject: ${subject}\n`;
        summaryText += `Reported Date: ${reportedDate}\n`;
        summaryText += `Description: ${incidentDescription}\n\n`;
      }

      // Establishment Details
      summaryText += `ESTABLISHMENT DETAILS:\n`;
      summaryText += `Name: ${establishmentNameEn}\n`;
      summaryText += `ID: ${establishmentId}\n`;
      summaryText += `License No: ${licenseNo}\n`;
      summaryText += `License Expiry: ${licenseExpiryDate}\n`;
      summaryText += `Type: ${establishmentTypeEn}\n`;
      summaryText += `Nationality: ${nationalityEn}\n\n`;

      // Location Details
      summaryText += `LOCATION DETAILS:\n`;
      summaryText += `Emirate: ${emirateEn}\n`;
      summaryText += `Zone: ${zoneEn}\n`;
      if (latitude !== 'N/A' && longitude !== 'N/A') {
        summaryText += `Coordinates: ${latitude}, ${longitude}\n\n`;
      }

      // Inspection Results
      summaryText += `INSPECTION SUMMARY:\n`;
      summaryText += `Total Questions: ${questions.length}\n`;
      summaryText += `Questions Answered: ${
        Object.keys(answers).length
      }\n`;
      summaryText += `Compliance: ${complianceCount}\n`;
      summaryText += `Non-Compliance: ${nonComplianceCount}\n\n`;

      // Add question summary
      summaryText += `QUESTION DETAILS:\n`;
      questions.forEach((question, index) => {
        const answer = answers[question.id] || 'Unanswered';
        const comment = comments[question.id]
          ? `\n   Comment: ${comments[question.id]}`
          : '';
        summaryText += `${index + 1}. ${
          question.question_en
        }: ${answer}${comment}\n`;
      });

      // Add violation details if any
      if (violationQuestions.length > 0) {
        summaryText += `\nVIOLATION DETAILS:\n`;
        violationQuestions.forEach((question, index) => {
          const reasonIds =
            selectedViolationReasons[question.id] || [];
          const reasonTexts = reasonIds.map(
            (id) =>
              violationReasons[id]?.display_name_en ||
              'Unknown reason'
          );

          summaryText += `${index + 1}. ${question.question_en}\n`;
          summaryText += `   Answer: ${
            answers[question.id] || 'N/A'
          }\n`;
          if (reasonTexts.length > 0) {
            summaryText += `   Reasons: ${reasonTexts.join(', ')}\n`;
          }
        });
      }
    } else {
      // Arabic version
      summaryText += `ملخص زيارة التفتيش\n`;
      summaryText += `=======================\n\n`;
      summaryText += `التاريخ والوقت: ${dateTimeStr}\n\n`;

      // Task Information in Arabic
      summaryText += `معلومات المهمة:\n`;
      summaryText += `معرف المهمة: ${taskId}\n`;
      summaryText += `نوع المهمة: ${taskTypeEn}\n`;
      summaryText += `نوع التفتيش: ${inspectionTypeEn}\n`;
      summaryText += `حالة المهمة: ${taskStatus}\n`;
      summaryText += `درجة المخاطرة: ${riskScore}\n\n`;

      // Incident Information in Arabic (if available)
      if (incidentDescription !== 'N/A' || subject !== 'N/A') {
        summaryText += `معلومات الحادث:\n`;
        summaryText += `الموضوع: ${subject}\n`;
        summaryText += `تاريخ الإبلاغ: ${reportedDate}\n`;
        summaryText += `الوصف: ${incidentDescription}\n\n`;
      }

      // Establishment Details in Arabic
      summaryText += `تفاصيل المنشأة:\n`;
      summaryText += `الاسم: ${
        establishmentNameAr || establishmentNameEn
      }\n`;
      summaryText += `الرقم التعريفي: ${establishmentId}\n`;
      summaryText += `رقم الرخصة: ${licenseNo}\n`;
      summaryText += `تاريخ انتهاء الرخصة: ${licenseExpiryDate}\n`;
      summaryText += `النوع: ${establishmentTypeEn}\n`;
      summaryText += `الجنسية: ${nationalityEn}\n\n`;

      // Location Details in Arabic
      summaryText += `تفاصيل الموقع:\n`;
      summaryText += `الإمارة: ${emirateEn}\n`;
      summaryText += `المنطقة: ${zoneEn}\n`;
      if (latitude !== 'N/A' && longitude !== 'N/A') {
        summaryText += `الإحداثيات: ${latitude}, ${longitude}\n\n`;
      }

      // Inspection Results in Arabic
      summaryText += `ملخص التفتيش:\n`;
      summaryText += `إجمالي الأسئلة: ${questions.length}\n`;
      summaryText += `الأسئلة المجابة: ${
        Object.keys(answers).length
      }\n`;
      summaryText += `الامتثال: ${complianceCount}\n`;
      summaryText += `عدم الامتثال: ${nonComplianceCount}\n\n`;

      // Add question summary in Arabic
      summaryText += `تفاصيل الأسئلة:\n`;
      questions.forEach((question, index) => {
        const answer = answers[question.id] || 'غير مجاب';
        const comment = comments[question.id]
          ? `\n   تعليق: ${comments[question.id]}`
          : '';
        summaryText += `${index + 1}. ${
          question.question_ar
        }: ${answer}${comment}\n`;
      });

      // Add violation details if any in Arabic
      if (violationQuestions.length > 0) {
        summaryText += `\nتفاصيل المخالفات:\n`;
        violationQuestions.forEach((question, index) => {
          const reasonIds =
            selectedViolationReasons[question.id] || [];
          const reasonTexts = reasonIds.map(
            (id) =>
              violationReasons[id]?.display_name_ar || 'سبب غير معروف'
          );

          summaryText += `${index + 1}. ${question.question_ar}\n`;
          summaryText += `   الإجابة: ${
            answers[question.id] || 'غير متوفر'
          }\n`;
          if (reasonTexts.length > 0) {
            summaryText += `   الأسباب: ${reasonTexts.join('، ')}\n`;
          }
        });
      }
    }

    return summaryText;
  };

  // Format seconds to HH:mm:ss
  const formatTime = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return [
      hours.toString().padStart(2, '0'),
      minutes.toString().padStart(2, '0'),
      seconds.toString().padStart(2, '0')
    ].join(':');
  };

  // Handle pause inspection
  const handlePauseInspection = async () => {
    try {
      setPauseLoading(true);

      // Call the pause API
      const response = await fetch(`${BASE_URL}/sdp/rules/api/v2/task/actions/pause`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth?.user?.access_token}`,
        },
        body: JSON.stringify({
          task_id: updatedTaskDetails?.id,
          params: JSON.stringify({
            inspection_type: updatedTaskDetails?.params?.inspection_type,
            emirate_id: updatedTaskDetails?.emirate_id,
            zone_id: updatedTaskDetails?.zone_id,
            inspection_type_id: updatedTaskDetails?.params?.inspection_type_id,
            establishment_id: updatedTaskDetails?.establishment_id,
            person_to_inspect: "",
            subject: updatedTaskDetails?.params?.subject,
            message: "",
            started_at: updatedTaskDetails?.params?.started_at,
            last_started_at: updatedTaskDetails?.params?.last_started_at,
            time_taken: secondsElapsed
          })
        }),
      });

      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Pause API response:', result);

      // Show success message
      showToast('success', language === 'en' ? 'Inspection paused successfully' : 'تم إيقاف التفتيش مؤقتًا بنجاح');

      // Redirect to plan page
      navigate('/plan');
    } catch (error) {
      console.error('Error pausing inspection:', error);
      showToast('error', language === 'en' ? 'Failed to pause inspection' : 'فشل في إيقاف التفتيش مؤقتًا');
    } finally {
      setPauseLoading(false);
      setShowPauseConfirmation(false);
    }
  };

  // Start timer on component mount
  useEffect(() => {
    // Start the timer
    timerIntervalRef.current = window.setInterval(() => {
      setSecondsElapsed(prev => prev + 1);
    }, 1000);

    // Clean up on unmount
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  // Auto-fill visit summary when summary view is shown
  useEffect(() => {
    if (showSummary && visitSummary === '') {
      const summary = generateVisitSummary();
      setVisitSummary(summary);
    }
  }, [showSummary]);

  // Helper function to truncate text
  const truncateText = (text: string, maxLength: number) => {
    if (!text) return '';
    return text.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text;
  };

  // Get the category of the current question
  // First try to get it from the categoryReference, then fall back to categories
  const currentCategory = currentQuestion
    ? categoryReference[currentQuestion.category_id] ||
      categories.find((c) => c.id === currentQuestion.category_id)
    : null;

  // Debug log to check category data
  useEffect(() => {
    if (currentQuestion) {
      console.log(
        'Current question category_id:',
        currentQuestion.category_id
      );
      console.log(
        'Category reference data for this ID:',
        categoryReference[currentQuestion.category_id]
      );
    }
  }, [currentQuestion, categoryReference]);
// bg-gradient-to-b from-white to-[#f8f3e9]
  if (loading || loadingChildQuestions || loadingViolationReasons) {
    return (
      <div className="h-full w-full pb-30 overflow-auto  flex items-center justify-center ">
        <div className="flex flex-col items-center">
          <div className="h-10 w-10 border-4 border-[#b89256] border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">
            {language === 'en'
              ? loadingChildQuestions
                ? 'Loading follow-up questions...'
                : loadingViolationReasons
                ? 'Loading violation reasons...'
                : 'Loading questionnaire...'
              : loadingChildQuestions
              ? 'جاري تحميل الأسئلة التابعة...'
              : loadingViolationReasons
              ? 'جاري تحميل أسباب المخالفات...'
              : 'جاري تحميل الاستبيان...'}
          </p>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="h-full w-full pb-30 overflow-auto  flex items-center justify-center p-4">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-[#b89256] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            {language === 'en'
              ? 'No Questions Available'
              : 'لا توجد أسئلة متاحة'}
          </h2>
          <p className="text-gray-600 mb-6">
            {language === 'en'
              ? 'No questions found for this inspection type.'
              : 'لم يتم العثور على أسئلة لهذا النوع من التفتيش.'}
          </p>
          <Button
            onClick={onClose}
            className="px-4 py-2 bg-[#b89256] text-white rounded-md hover:bg-[#a17d45]"
          >
            {language === 'en' ? 'Go Back' : 'العودة'}
          </Button>
        </div>
      </div>
    );
  }

  // Render violation selection view
  if (showViolationSelection && !showFineReview && !showSummary) {
    return (
      <div className="h-full w-full pb-30 overflow-auto flex flex-col">
        <div className="p-4 bg-[#f8f3e9] border-b border-[#e9dcc7]">
          <h1 className="text-xl font-bold text-gray-900">
            {language === 'en'
              ? 'Violation Details'
              : 'تفاصيل المخالفات'}
          </h1>
          <p className="text-gray-600">
            {language === 'en'
              ? 'Select a reason for each violation'
              : 'اختر سببًا لكل مخالفة'}
          </p>
        </div>
        <div className="flex-1 overflow-auto p-4">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
            <div className="p-3 bg-amber-50 border-b border-amber-200">
              <h3 className="font-medium text-amber-800">
                {language === 'en'
                  ? 'Violations Detected'
                  : 'تم اكتشاف مخالفات'}
              </h3>
              <p className="text-sm text-amber-700">
                {language === 'en'
                  ? 'Please select a reason for each violation below'
                  : 'يرجى اختيار سبب لكل مخالفة أدناه'}
              </p>
            </div>

            <div className="divide-y divide-gray-200">
              {violationQuestions.map((question) => {
                return (
                  <div key={question.id} className="p-4">
                    <div className="mb-3">
                      <h4 className="font-medium text-gray-800">
                        {language === 'en'
                          ? question.question_en
                          : question.question_ar}
                      </h4>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                          {language === 'en' ? 'Violation' : 'مخالفة'}
                        </span>
                        {/* {question.fine_amount && (
                          <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full">
                            {language === 'en'
                              ? `Fine: ${question.fine_amount} AED`
                              : `غرامة: ${question.fine_amount} درهم`}
                          </span>
                        )} */}
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === 'en'
                          ? 'Select Violation Reason'
                          : 'اختر سبب المخالفة'}
                      </label>

                      <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-300 rounded-lg p-2">
                        {Object.values(violationReasons).map(
                          (reason) => {
                            const isSelected =
                              selectedViolationReasons[
                                question.id
                              ]?.includes(reason.id) || false;
                            return (
                              <div
                                key={reason.id}
                                className="flex items-start"
                              >
                                <input
                                  type="checkbox"
                                  id={`reason-${question.id}-${reason.id}`}
                                  checked={isSelected}
                                  onChange={(e) =>
                                    handleViolationReasonSelect(
                                      question.id,
                                      reason.id,
                                      e.target.checked
                                    )
                                  }
                                  className="mt-1 mr-2"
                                />
                                <label
                                  htmlFor={`reason-${question.id}-${reason.id}`}
                                  className="text-sm text-gray-700 cursor-pointer"
                                >
                                  {language === 'en'
                                    ? reason.display_name_en
                                    : reason.display_name_ar}
                                </label>
                              </div>
                            );
                          }
                        )}
                      </div>

                      {/* Display selected reasons */}
                      {selectedViolationReasons[question.id]?.length >
                        0 && (
                        <div className="mt-2">
                          <div className="text-xs font-medium text-gray-500 mb-1">
                            {language === 'en'
                              ? 'Selected reasons:'
                              : 'الأسباب المختارة:'}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {selectedViolationReasons[
                              question.id
                            ]?.map((reasonId) => (
                              <span
                                key={reasonId}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-800"
                              >
                                {language === 'en'
                                  ? violationReasons[reasonId]
                                      ?.display_name_en
                                  : violationReasons[reasonId]
                                      ?.display_name_ar}
                                <button
                                  onClick={() =>
                                    handleViolationReasonSelect(
                                      question.id,
                                      reasonId,
                                      false
                                    )
                                  }
                                  className="ml-1 text-amber-600 hover:text-amber-800"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="fixed bottom-[4rem] left-0 right-0 px-4 py-3 border-t border-gray-200 bg-[#f8f3e9] flex items-center justify-between z-10">
          {/* <Button
            onClick={() => {
              setShowViolationSelection(false);
              setShowSummary(true);
            }}
            variant="outline"
            className="px-4 py-2 border border-gray-300 rounded-md flex items-center space-x-1 text-gray-700 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>
              {language === 'en'
                ? 'Back to Summary'
                : 'العودة إلى الملخص'}
            </span>
          </Button> */}

          <Button
            onClick={() => {
              setShowViolationSelection(false);
              goToPreviousQuestion();
            }}
            variant="outline"
            className="px-4 py-2 border border-gray-300 rounded-md flex items-center space-x-1 text-gray-700 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>
              {language === 'en'
                ? 'Back to Questions'
                : 'العودة إلى الأسئلة'}
            </span>
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={
              submitting ||
              violationQuestions.some(
                (q) => !selectedViolationReasons[q.id]
              )
            }
            title={
              violationQuestions.some(
                (q) => !selectedViolationReasons[q.id]
              )
                ? language === 'en'
                  ? 'Please select a reason for each violation'
                  : 'يرجى اختيار سبب لكل مخالفة'
                : ''
            }
            className="px-4 py-2 bg-[#b89256] rounded-md flex items-center space-x-1 text-white hover:bg-[#a17d45] shadow-sm disabled:opacity-70"
          >
            {submitting ? (
              <>
                <span>
                  {language === 'en'
                    ? 'Submitting...'
                    : 'جاري التقديم...'}
                </span>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin ml-1"></div>
              </>
            ) : (
              <>
                <span>
                  {language === 'en'
                    ? 'Continue to Summary'
                    : 'التحقق من الغرامات'}
                </span>
                <CheckCircle className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Render success modal
  if (showSuccessModal) {
    return (
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate('/plan');
        }}
        points={pointsEarned}
        language={language}
      />
    );
  }

  // Render fine review view
  if (showFineReview && !showViolationSelection && !showSummary) {
    return (
      <div className="h-full w-full  pb-30 overflow-auto  flex flex-col">
        <div className="p-4 bg-[#f8f3e9] border-b border-[#e9dcc7]">
          <h1 className="text-xl font-bold text-gray-900">
            {language === 'en' ? 'Fine Summary' : 'ملخص الغرامة'}
          </h1>
          <p className="text-gray-600">
            {language === 'en'
              ? 'Review fines before final submission'
              : 'مراجعة الغرامات قبل التقديم النهائي'}
          </p>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {fineInfo &&
          (fineInfo.fine_amount || fineInfo.total_fine) ? (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
              <div className="p-3 bg-red-50 border-b border-red-200">
                <h3 className="font-medium text-red-800">
                  {language === 'en'
                    ? 'Fines Summary'
                    : 'ملخص الغرامات'}
                </h3>
              </div>

              <div className="p-4">
                {violationQuestions.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      {language === 'en'
                        ? 'Violation Details'
                        : 'تفاصيل المخالفات'}
                    </h4>

                    {/* Fine Summary Table */}
                    <div className="grid grid-cols-1 gap-4">
                      {violationQuestions.map((question) => {
                        const reasonIds =
                          selectedViolationReasons[question.id] || [];

                        // Display multiple reasons if selected
                        const reasonTexts = reasonIds.map(
                          (id) =>
                            violationReasons[id]?.[
                              language === 'en'
                                ? 'display_name_en'
                                : 'display_name_ar'
                            ] || 'Unknown reason'
                        );

                        const reasonText =
                          reasonIds.length > 0
                            ? reasonTexts.join(', ')
                            : 'No reason selected';

                        // Get fine type from the first reason (if any)
                        const fineTypeId =
                          reasonIds.length > 0
                            ? violationReasons[reasonIds[0]]
                                ?.fine_type_id
                            : undefined;
                        const fineType = fineTypeId
                          ? `Type ${fineTypeId}`
                          : '-';

                        return (
                          <div
                            key={question.id}
                            className="rounded-2xl border border-gray-200 shadow-sm p-4 bg-white"
                          >
                            <div className="text-sm text-gray-500 mb-2">
                              {language === 'en'
                                ? 'Question ID'
                                : 'معرف السؤال'}
                            </div>
                            <div className=" font-semibold text-gray-800 mb-4">
                              {language === 'en'
                                ? question.question_en
                                : question.question_ar}
                            </div>

                            <div className="mb-2">
                              <div className="text-sm text-gray-500">
                                {language === 'en'
                                  ? 'Violation Reason'
                                  : 'سبب المخالفة'}
                              </div>
                              <div className="text-sm font-medium text-gray-700">
                                {reasonTexts.map(
                                  (reasonText, index) => (
                                    <div
                                      key={index}
                                      className="text-sm font-medium text-gray-700"
                                    >
                                      {reasonText}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>

                            {/* <div className="mb-2">
                              <div className="text-sm text-gray-500">
                                {language === 'en'
                                  ? 'Fine Type'
                                  : 'نوع الغرامة'}
                              </div>
                              <div className="text-sm font-medium text-gray-700">
                                {fineType}
                              </div>
                            </div>

                            <div className="mb-2">
                              <div className="text-sm text-gray-500">
                                {language === 'en'
                                  ? 'Fine Amount'
                                  : 'مبلغ الغرامة'}
                              </div>
                              <div className="text-sm font-semibold text-red-600">
                                {question.fine_amount
                                  ? `${question.fine_amount.toLocaleString()} AED`
                                  : '-'}
                              </div>
                            </div> */}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {fineInfo.total_fine && (
                  <div className="mt-6 p-4 bg-red-50 border border-red-100 rounded-lg">
                    <div className="flex justify-between items-center">
                      <h4 className="font-bold text-gray-900">
                        {language === 'en'
                          ? 'Total Fine'
                          : 'إجمالي الغرامة'}
                      </h4>
                      <p className="text-xl font-bold text-red-600">
                        {fineInfo.total_fine.toLocaleString()} AED
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-green-50 p-6 rounded-lg border border-green-100 text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-green-800 mb-2">
                {language === 'en'
                  ? '✅ No fines detected'
                  : '✅ لم يتم اكتشاف غرامات'}
              </h3>
              <p className="text-green-600">
                {language === 'en'
                  ? 'Your inspection report has been processed without any fines.'
                  : 'تمت معالجة تقرير التفتيش الخاص بك بدون أي غرامات.'}
              </p>
            </div>
          )}
        </div>

        <div className="fixed bottom-[4rem] left-0 right-0 px-4 py-3 border-t border-gray-200 bg-[#f8f3e9] flex items-center justify-between z-10">
          <Button
            onClick={() => {
              setShowFineReview(false);
              setShowViolationSelection(true);
            }}
            variant="outline"
            className="px-4 py-2 border border-gray-300 rounded-md flex items-center space-x-1 text-gray-700 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>
              {language === 'en'
                ? 'Back to Violations'
                : 'العودة إلى المخالفات'}
            </span>
          </Button>

          <Button
            onClick={() => {
              setShowFineReview(false);
              setShowSummary(true);
            }}
            disabled={submitting}
            className="px-4 py-2 bg-[#b89256] rounded-md flex items-center space-x-1 text-white hover:bg-[#a17d45] shadow-sm disabled:opacity-70"
          >
            {submitting ? (
              <>
                <span>
                  {language === 'en'
                    ? 'Submitting...'
                    : 'جاري التقديم...'}
                </span>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin ml-1"></div>
              </>
            ) : (
              <>
                <span>
                  {language === 'en'
                    ? 'Continue to Summary'
                    : 'الانتقال إلى الملخص'}
                </span>
                <CheckCircle className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Render summary view
  if (showSummary) {
    const answeredQuestions = Object.keys(answers).length;
    const mandatoryQuestions = questions.filter(
      (q) => q.is_mandatory
    ).length;
    const answeredMandatory = questions
      .filter((q) => q.is_mandatory)
      .filter((q) => answers[q.id]).length;

    return (
      <div className="h-full w-full  pb-30 overflow-auto  flex flex-col">
        <div className="p-4 bg-[#f8f3e9] border-b border-[#e9dcc7]">
          <h1 className="text-xl font-bold text-gray-900">
            {language === 'en'
              ? 'Inspection Summary'
              : 'ملخص التفتيش'}
          </h1>
          <p className="text-gray-600">
            {language === 'en'
              ? 'Review your answers before submission'
              : 'مراجعة إجاباتك قبل التقديم'}
          </p>
        </div>

        <div className="flex-1 overflow-auto p-4">
          <div className="mb-6 grid grid-cols-2 gap-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-100">
              <h3 className="font-medium text-green-800 mb-1">
                {language === 'en' ? 'Completed' : 'مكتمل'}
              </h3>
              <p className="text-2xl font-bold text-green-600">
                {answeredQuestions} / {questions.length}
              </p>
              <p className="text-sm text-green-700 mt-1">
                {language === 'en'
                  ? 'Questions answered'
                  : 'الأسئلة المجابة'}
              </p>
            </div>

            <div className="bg-[#f8f3e9] p-4 rounded-lg border border-[#e9dcc7]">
              <h3 className="font-medium text-[#8a6d3b] mb-1">
                {language === 'en' ? 'Mandatory' : 'إلزامي'}
              </h3>
              <p className="text-2xl font-bold text-[#b89256]">
                {answeredMandatory} / {mandatoryQuestions}
              </p>
              <p className="text-sm text-[#8a6d3b] mt-1">
                {language === 'en'
                  ? 'Required questions'
                  : 'الأسئلة المطلوبة'}
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-3 bg-gray-50 border-b border-gray-200">
              <h3 className="font-medium text-gray-800">
                {language === 'en'
                  ? 'Question Summary'
                  : 'ملخص الأسئلة'}
              </h3>
            </div>

            <div className="divide-y divide-gray-200">
              {questions.map((question) => {
                const answer = answers[question.id];
                let answerColor = 'text-gray-400';
                let answerBg = 'bg-gray-50';

                if (answer === 'Yes') {
                  answerColor = 'text-green-600';
                  answerBg = 'bg-green-50';
                } else if (answer === 'No') {
                  answerColor = 'text-red-600';
                  answerBg = 'bg-red-50';
                } else if (answer === 'N/A') {
                  answerColor = 'text-[#b89256]';
                  answerBg = 'bg-[#f8f3e9]';
                }

                return (
                  <div
                    key={question.id}
                    className="p-3 hover:bg-gray-50"
                  >
                    <div className="flex items-start">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">
                          {language === 'en'
                            ? question.question_en
                            : question.question_ar}
                          {question.is_mandatory && (
                            <span className="text-red-500 ml-1">
                              *
                            </span>
                          )}
                        </p>

                        {comments[question.id] && (
                          <p className="text-xs text-gray-500 mt-1">
                            {language === 'en'
                              ? 'Comment: '
                              : 'تعليق: '}
                            {comments[question.id]}
                          </p>
                        )}
                      </div>

                      <div
                        className={`ml-3 px-3 py-1 rounded-full text-xs font-medium ${answerBg} ${answerColor}`}
                      >
                        {answer ||
                          (language === 'en'
                            ? 'Unanswered'
                            : 'غير مجاب')}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* violation summary */}

          <div className="flex-1 overflow-auto p-4 hidden">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
              <div className="p-3 bg-amber-50 border-b border-amber-200">
                <h3 className="font-medium text-amber-800">
                  {language === 'en'
                    ? 'Violations Detected'
                    : 'تم اكتشاف مخالفات'}
                </h3>
                <p className="text-sm text-amber-700">
                  {language === 'en'
                    ? 'Please select a reason for each violation below'
                    : 'يرجى اختيار سبب لكل مخالفة أدناه'}
                </p>
              </div>

              <div className="divide-y divide-gray-200 hidden">
                {violationQuestions.map((question) => {
                  return (
                    <div key={question.id} className="p-4">
                      <div className="mb-3">
                        <h4 className="font-medium text-gray-800">
                          {language === 'en'
                            ? question.question_en
                            : question.question_ar}
                        </h4>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                            {language === 'en'
                              ? 'Violation'
                              : 'مخالفة'}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4">
                        {/* Display selected reasons */}
                        {selectedViolationReasons[question.id]
                          ?.length > 0 && (
                          <div className="mt-2">
                            <div className="text-xs font-medium text-gray-500 mb-1">
                              {language === 'en'
                                ? 'Selected reasons:'
                                : 'الأسباب المختارة:'}
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {selectedViolationReasons[
                                question.id
                              ]?.map((reasonId) => (
                                <span
                                  key={reasonId}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-800"
                                >
                                  {language === 'en'
                                    ? violationReasons[reasonId]
                                        ?.display_name_en
                                    : violationReasons[reasonId]
                                        ?.display_name_ar}
                                  <button
                                    onClick={() =>
                                      handleViolationReasonSelect(
                                        question.id,
                                        reasonId,
                                        false
                                      )
                                    }
                                    className="ml-1 text-amber-600 hover:text-amber-800"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Fine summary */}

          <div className="flex-1 overflow-auto p-4">
            {fineInfo &&
            (fineInfo.fine_amount || fineInfo.total_fine) ? (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
                <div className="p-3 bg-red-50 border-b border-red-200">
                  <h3 className="font-medium text-red-800">
                    {language === 'en'
                      ? 'Fines Summary'
                      : 'ملخص الغرامات'}
                  </h3>
                </div>

                <div className="p-4">
                  {violationQuestions.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        {language === 'en'
                          ? 'Violation Details'
                          : 'تفاصيل المخالفات'}
                      </h4>

                      {/* Fine Summary Table */}
                      <div className="grid grid-cols-1 gap-4">
                        {violationQuestions.map((question) => {
                          const reasonIds =
                            selectedViolationReasons[question.id] ||
                            [];

                          // Display multiple reasons if selected
                          const reasonTexts = reasonIds.map(
                            (id) =>
                              violationReasons[id]?.[
                                language === 'en'
                                  ? 'display_name_en'
                                  : 'display_name_ar'
                              ] || 'Unknown reason'
                          );

                          const reasonText =
                            reasonIds.length > 0
                              ? reasonTexts.join(', ')
                              : 'No reason selected';

                          // Get fine type from the first reason (if any)
                          const fineTypeId =
                            reasonIds.length > 0
                              ? violationReasons[reasonIds[0]]
                                  ?.fine_type_id
                              : undefined;
                          const fineType = fineTypeId
                            ? `Type ${fineTypeId}`
                            : '-';

                          return (
                            <div
                              key={question.id}
                              className="rounded-2xl border border-gray-200 shadow-sm p-4 bg-white"
                            >
                              <div className="text-sm text-gray-500 mb-2">
                                {language === 'en'
                                  ? 'Question ID'
                                  : 'معرف السؤال'}
                              </div>
                              <div className=" font-semibold text-gray-800 mb-4">
                                {language === 'en'
                                  ? question.question_en
                                  : question.question_ar}{' '}
                                - {answers[question.id]}
                              </div>

                              <div className="mb-2">
                                <div className="text-sm text-gray-500">
                                  {language === 'en'
                                    ? 'Violation Reason'
                                    : 'سبب المخالفة'}
                                </div>
                                <div className="text-sm font-medium text-gray-700">
                                  {reasonTexts.map(
                                    (reasonText, index) => (
                                      <div
                                        key={index}
                                        className="text-sm font-medium text-gray-700"
                                      >
                                        {reasonText} - AED{' '}
                                        {question.fines?.[index]}
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>

                              {/* <div className="mb-2">
                                <div className="text-sm text-gray-500">
                                  {language === 'en'
                                    ? 'Fine Type'
                                    : 'نوع الغرامة'}
                                </div>
                                <div className="text-sm font-medium text-gray-700">
                                  {fineType}
                                </div>
                              </div> */}

                              {/* <div className="mb-2">
                                <div className="text-sm text-gray-500">
                                  {language === 'en'
                                    ? 'Fine Amount'
                                    : 'مبلغ الغرامة'}
                                </div>
                                <div className="text-sm font-semibold text-red-600">
                                  {question.fine_amount
                                    ? `${question.fine_amount.toLocaleString()} AED`
                                    : '-'}
                                </div>
                              </div> */}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {fineInfo.total_fine && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-100 rounded-lg hidden">
                      <div className="flex justify-between items-center">
                        <h4 className="font-bold text-gray-900">
                          {language === 'en'
                            ? 'Total Fine'
                            : 'إجمالي الغرامة'}
                        </h4>
                        <p className="text-xl font-bold text-red-600">
                          {fineInfo.total_fine.toLocaleString()} AED
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-green-50 p-6 rounded-lg border border-green-100 text-center hidden">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-green-800 mb-2">
                  {language === 'en'
                    ? '✅ No fines detected'
                    : '✅ لم يتم اكتشاف غرامات'}
                </h3>
                <p className="text-green-600">
                  {language === 'en'
                    ? 'Your inspection report has been processed without any fines.'
                    : 'تمت معالجة تقرير التفتيش الخاص بك بدون أي غرامات.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Visit Summary and Signature Section */}
        <div className="p-4 border-t border-gray-200">
          {/* <h3 className="text-lg font-medium mb-4">
            {language === 'en' ? 'Visit Summary' : 'ملخص الزيارة'}
          </h3> */}

          <div className="mb-6">
            <label
              htmlFor="inspectorComment"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {language === 'en'
                ? 'Inspector Comment'
                : 'ملخص الزيارة'}
            </label>
            <textarea
              id="inspectorComment"
              rows={5}
              className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-[#b89256] focus:border-[#b89256] font-mono text-sm"
              placeholder={
                language === 'en'
                  ? 'Enter comments...'
                  : 'أدخل ملخص الزيارة...'
              }
              value={inspectorComment}
              onChange={(e) => setInspectorComment(e.target.value)}
            />
          </div>

          {/* Signature Pad */}
          <div className="mb-6">
            <SignatureCanvas
              value={signature}
              onChange={setSignature}
              height={150}
              label={language === 'en' ? 'Signature' : 'التوقيع'}
              clearLabel={
                language === 'en' ? 'Clear Signature' : 'مسح التوقيع'
              }
            />
          </div>
        </div>

        {/* Report Generation Section */}
        <div className="mb-6 mx-4 p-4 border border-gray-200 rounded-lg bg-white">
          <h2 className="text-lg font-semibold mb-3">
            {language === 'en'
              ? 'Inspection Report'
              : 'تقرير التفتيش'}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            {language === 'en'
              ? 'Generate and view the inspection report based on your answers.'
              : 'إنشاء وعرض تقرير التفتيش بناءً على إجاباتك.'}
          </p>
          <div className="flex space-x-3">
            {/* <Button
              onClick={() => {
                // Generate a summary of the inspection
                generateVisitSummary();
                // Show a toast notification
                showToast(
                  'success',
                  language === 'en'
                    ? 'Report generated successfully'
                    : 'تم إنشاء التقرير بنجاح'
                );
              }}
              className="px-4 py-2 bg-[#b89256] rounded-md flex items-center space-x-1 text-white hover:bg-[#a17d45] shadow-sm"
            >
              <span>
                {language === 'en'
                  ? 'Generate Report'
                  : 'إنشاء التقرير'}
              </span>
            </Button> */}
            <Button
              onClick={() => {
                viewReportPdf();
              }}
              variant="outline"
              className="px-4 py-2 border border-gray-300 rounded-md flex items-center space-x-1 text-gray-700 hover:bg-gray-100"
            >
              <span>
                {language === 'en' ? 'View Report' : 'عرض التقرير'}
              </span>
            </Button>
          </div>
          {pdfUrl && (
            <div className="mt-4">
              {/* Use our simple PDF viewer component with close button */}
              <SimplePdfViewer
                pdfUrl={pdfUrl}
                height={600}
                onClose={() => {
                  if (pdfUrl) {
                    URL.revokeObjectURL(pdfUrl); // Clean up the blob URL
                  }
                  setPdfUrl(''); // Clear the PDF URL to hide the viewer
                }}
              />
              <div className="mt-2 flex justify-end">
                <a
                  href={pdfUrl}
                  download="inspection-report.pdf"
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {language === 'en' ? 'Download PDF' : 'تحميل PDF'}
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="fixed bottom-[4rem] left-0 right-0 px-4 py-3 border-t border-gray-200 bg-[#f8f3e9] flex items-center justify-between z-10">
          <Button
            onClick={goToPreviousQuestion}
            variant="outline"
            className="px-4 py-2 border border-gray-300 rounded-md flex items-center space-x-1 text-gray-700 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>
              {language === 'en'
                ? 'Back to Questions'
                : 'العودة إلى الأسئلة'}
            </span>
          </Button>

          <Button
            onClick={handleFinalSubmit}
            disabled={submitting}
            className="px-4 py-2 bg-[#b89256] rounded-md flex items-center space-x-1 text-white hover:bg-[#a17d45] shadow-sm disabled:opacity-70"
          >
            {submitting ? (
              <>
                <span>
                  {language === 'en'
                    ? 'Submitting...'
                    : 'جاري التقديم...'}
                </span>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin ml-1"></div>
              </>
            ) : (
              <>
                <span>
                  {language === 'en'
                    ? 'Submit Inspection Report'
                    : 'إرسال تقرير التفتيش'}
                </span>
                <CheckCircle className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Parse multiple choices
  const choices = currentQuestion?.[
    language === 'ar' ? 'multiple_choices_ar' : 'multiple_choices_en'
  ]
    ? currentQuestion[
        language === 'ar'
          ? 'multiple_choices_ar'
          : 'multiple_choices_en'
      ].split('-')
    : ['Yes', 'No', 'N/A'];

  // Standard view
  return (
    <div className="h-full w-full  pb-30 overflow-auto  flex flex-col">
      {/* Progress bar */}
      <div className="h-1 bg-gray-200 hidden">
        <div className="h-1 bg-gradient-to-r from-gold-700 to-gold-300"
          style={{
            width: `${
              ((currentQuestionIndex + 1) / questions.length) * 100
            }%`,
          }}
        ></div>
      </div>

      {/* Question Navigation Dropdown */}
      <div className="px-4 py-2 bg-[#f8f3e9] border-b border-[#e9dcc7]">
        <div className="flex items-center px-2 py-1 rounded-lg absolute top-[1rem] right-[1rem]">
            <Clock className="h-6 w-6 text-gray-400 mr-2" />
            <span className="text-3xl font-medium">{formatTime(secondsElapsed)}</span>
            <Button
              variant="ghost"
              size="sm"
              className="ml-2 text-xs pl-1 pr-1 rounded-md"
              onClick={() => setShowPauseConfirmation(true)}
            >
              <PauseCircle className="h-8 w-8" />
            </Button>
        </div>
        <div className='flex items-center justify-between'>
          <div className="question-navigation w-full">
            <label
              htmlFor="question-select"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {language === 'en'
                ? 'Jump to Question:'
                : 'الانتقال إلى السؤال:'}
            </label>
            <select
              id="question-select"
              value={currentQuestionIndex}
              onChange={(e) =>
                navigateToQuestion(parseInt(e.target.value))
              }
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#b89256] focus:border-[#b89256]"
            >
              {questions.map((question, index) => {
                const isViewed = viewedQuestions.has(question.id);
                const isAnswered = !!answers[question.id];

                return (
                  <option
                    key={question.id}
                    value={index}
                    className={
                      isAnswered
                        ? 'text-green-600 font-medium'
                        : isViewed
                        ? 'text-blue-600'
                        : ''
                    }
                  >
                    {index + 1}.{' '}
                    {truncateText(
                      language === 'en'
                        ? question.question_en
                        : question.question_ar,
                      40
                    )}
                    {isAnswered ? ' ✓' : ''}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Pause Confirmation Dialog */}
          {showPauseConfirmation && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-sm w-full">
                <h3 className="text-lg font-medium mb-4">
                  {language === 'en' ? 'Do you want to pause this inspection?' : 'هل تريد إيقاف هذا التفتيش مؤقتًا؟'}
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {language === 'en'
                    ? 'Your progress will be saved and you can continue later.'
                    : 'سيتم حفظ تقدمك ويمكنك المتابعة لاحقًا.'}
                </p>
                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowPauseConfirmation(false)}
                    disabled={pauseLoading}
                  >
                    {language === 'en' ? 'Cancel' : 'إلغاء'}
                  </Button>
                  <Button
                    onClick={handlePauseInspection}
                    disabled={pauseLoading}
                    className="bg-[#b89256] hover:bg-[#a17d45] text-white"
                  >
                    {pauseLoading ? (
                      <>
                        <span>{language === 'en' ? 'Pausing...' : 'جاري الإيقاف...'}</span>
                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin ml-1"></div>
                      </>
                    ) : (
                      language === 'en' ? 'Pause Inspection' : 'إيقاف التفتيش'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {/* Show parent question path if this is a child question */}
        {currentQuestion?.parent_question_id && (
          <div className="mb-4 p-2 bg-[#f8f3e9] rounded-md border border-[#e9dcc7]">
            <p className="text-sm text-[#8a6d3b]">
              {language === 'en'
                ? 'Follow-up question based on your previous answer'
                : 'سؤال متابعة بناءً على إجابتك السابقة'}
            </p>
          </div>
        )}
        <div className="mb-6">
          <h2 className="text-[#b89256] font-medium">
            {language === 'en'
              ? currentCategory?.display_name_en || 'Inspection'
              : currentCategory?.display_name_ar || 'التفتيش'}
          </h2>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">
            {currentQuestionIndex + 1}.{' '}
            {language === 'en'
              ? currentQuestion?.question_en
              : currentQuestion?.question_ar}
            {currentQuestion?.is_mandatory && (
              <span className="text-red-500 ml-1">*</span>
            )}
          </h1>
        </div>

        {/* Show different input types based on answer_type_en */}
        {currentQuestion?.answer_type_en === 'Text' ? (
          <div className="mb-8">
            <div className="relative">
              <input
                type="text"
                value={answers[currentQuestion.id] || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only update the input value in state for UI responsiveness
                  // but don't trigger handleAnswer automatically
                  setAnswers((prev) => ({
                    ...prev,
                    [currentQuestion.id]: value,
                  }));
                }}
                placeholder={
                  language === 'en'
                    ? 'Enter your answer here...'
                    : 'أدخل إجابتك هنا...'
                }
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#b89256] focus:border-[#b89256]"
              />
              <button
                onClick={() =>
                  handleAnswer(
                    currentQuestion.id,
                    answers[currentQuestion.id] || ''
                  )
                }
                className="absolute right-3 top-1/2 transform -translate-y-1/2 px-3 py-1 bg-[#b89256] text-white rounded-md hover:bg-[#a17d45] text-sm"
              >
                {language === 'en' ? 'Validate' : 'تحقق'}
              </button>
            </div>
          </div>
        ) : currentQuestion?.answer_type_en === 'Number' ? (
          <div className="mb-8">
            <div className="relative">
              <input
                type="number"
                value={answers[currentQuestion.id] || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only update the input value in state for UI responsiveness
                  // but don't trigger handleAnswer automatically
                  setAnswers((prev) => ({
                    ...prev,
                    [currentQuestion.id]: value,
                  }));
                }}
                placeholder={
                  language === 'en'
                    ? 'Enter a number...'
                    : 'أدخل رقمًا...'
                }
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#b89256] focus:border-[#b89256]"
              />
              <button
                onClick={() =>
                  handleAnswer(
                    currentQuestion.id,
                    answers[currentQuestion.id] || ''
                  )
                }
                className="absolute right-3 top-1/2 transform -translate-y-1/2 px-3 py-1 bg-[#b89256] text-white rounded-md hover:bg-[#a17d45] text-sm"
              >
                {language === 'en' ? 'Validate' : 'تحقق'}
              </button>
            </div>
          </div>
        ) : currentQuestion?.answer_type_en === 'List' ? (
          <div className="grid grid-cols-3 gap-4 mb-8">
            {choices.map((choice, choiceIndex) => {
              const isSelected =
                answers[currentQuestion.id] === choice.trim();

              // Use the same color scheme as the multiple choice buttons
              let bgColor = isSelected
                ? 'bg-[#f8f3e9]'
                : 'bg-gray-100';
              let textColor = isSelected
                ? 'text-[#b89256]'
                : 'text-gray-600';
              let borderColor = isSelected
                ? 'border-[#b89256]'
                : 'border-gray-200';

              return (
                <button
                  key={choiceIndex}
                  onClick={() => {
                    // For List type, allow only one answer
                    handleAnswer(currentQuestion.id, choice.trim());
                  }}
                  className={`flex flex-col items-center justify-center p-4 border rounded-lg ${borderColor} ${
                    isSelected ? bgColor : 'hover:bg-gray-50'
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full ${bgColor} flex items-center justify-center mb-2`}
                  >
                    {/* Use consistent icon styling for all choices */}
                    <CheckCircle className={`h-6 w-6 ${textColor}`} />
                  </div>
                  <span className="font-medium">
                    {language === 'en'
                      ? choice.trim()
                      : choice.trim()}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4 mb-8">
            {choices.map((choice, index) => {
              let bgColor = 'bg-gray-100';
              let textColor = 'text-gray-600';
              let iconColor = 'text-gray-500';

              if (choice.trim() === 'Yes') {
                bgColor = 'bg-green-100';
                textColor = 'text-green-600';
                iconColor = 'text-green-600';
              } else if (choice.trim() === 'No') {
                bgColor = 'bg-red-100';
                textColor = 'text-red-600';
                iconColor = 'text-red-600';
              } else if (
                choice.trim() === 'N/A' ||
                choice.trim() === 'Not Applicable'
              ) {
                bgColor = 'bg-[#f8f3e9]';
                textColor = 'text-[#b89256]';
                iconColor = 'text-[#b89256]';
              }

              return (
                <button
                  key={index}
                  onClick={() =>
                    handleAnswer(currentQuestion!.id, choice.trim())
                  }
                  className={`flex flex-col items-center justify-center p-4 border rounded-lg ${
                    answers[currentQuestion!.id] === choice.trim()
                      ? `border-${textColor.replace(
                          'text-',
                          ''
                        )} ${bgColor.replace('100', '50')}`
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <AnswerOptionButton
                    choice={choice.trim()}
                    isSelected={
                      answers[currentQuestion!.id] === choice.trim()
                    }
                    onClick={() =>
                      handleAnswer(currentQuestion!.id, choice.trim())
                    }
                    size="lg"
                  />
                  <span className="font-medium">
                    {language === 'en'
                      ? choice.trim()
                      : choice.trim() === 'Yes'
                      ? 'نعم'
                      : choice.trim() === 'No'
                      ? 'لا'
                      : 'غير متوفر'}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {language === 'en' ? 'Comments' : 'تعليقات'}
          </h3>
          <textarea
            value={comments[currentQuestion!.id] || ''}
            onChange={(e) =>
              handleCommentChange(currentQuestion!.id, e.target.value)
            }
            placeholder={
              language === 'en'
                ? 'Add any additional comments...'
                : 'أضف أي تعليقات إضافية...'
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#b89256] focus:border-[#b89256] min-h-[120px]"
          ></textarea>
        </div>

        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {language === 'en' ? 'Photo Evidence' : 'دليل الصور'}
          </h3>
          <div className="flex space-x-4">
            <Button
              className="flex-1 flex items-center justify-center space-x-2 p-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
              variant="outline"
              onClick={() => setShowCamera(true)}
            >
              <Camera className="h-5 w-5 text-gray-600" />
              <span className="font-medium text-gray-700">
                {language === 'en' ? 'Take Photo' : 'التقاط صورة'}
              </span>
            </Button>
            <Button
              className="flex-1 flex items-center justify-center space-x-2 p-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
              variant="outline"
              onClick={() => setShowViolatedWorkerCamera(true)}
            >
              <AlertCircle className="h-5 w-5 text-red-600" />
              <span className="font-medium text-gray-700">
                {language === 'en' ? 'Check violated worker' : 'التحقق من العامل المخالف'}
              </span>
            </Button>
            {photoEvidence.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {photoEvidence.map((photo, index) => (
                  <img
                    key={index}
                    src={photo}
                    alt={`Evidence ${index + 1}`}
                    className="w-24 h-24 object-cover rounded border border-gray-300"
                  />
                ))}
              </div>
            )}
            {/* <Button
              className="flex-1 flex items-center justify-center space-x-2 p-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
              variant="outline"
            >
              <Upload className="h-5 w-5 text-gray-600" />
              <span className="font-medium text-gray-700">
                {language === 'en' ? 'Upload Image' : 'تحميل صورة'}
              </span>
            </Button> */}
          </div>
        </div>

        {/* Camera Modal */}
        {showCamera && (
          <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
            <div className="bg-black rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto">
              <div className="flex justify-end p-2">
                <Button
                  variant="ghost"
                  onClick={() => setShowCamera(false)}
                  className="rounded-full p-2 bg-white"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
              <InspectionCamera
                userName={auth.user?.profile.name || 'Inspector'}
                inspectionId={taskDetails?.id?.toString() || 'unknown'}
                backendUrl="/sdp/api/v1/document/upload_document"
                establishmentId={updatedTaskDetails?.establishment_id || ''}
                requestId={taskDetails?.id?.toString() || 'unknown'}
                documentCategory="inspection"
                documentType="evidence"
                notes="Inspection photo evidence"
                ocrRequired="false"
                onUploadSuccess={(response) => {
                  console.log(response)
                  setShowCamera(false);
                  toast.success(language === 'en' ? 'Photo uploaded successfully' : 'تم تحميل الصورة بنجاح');
                }}
                onUploadError={(error) => {
                  toast.error(language === 'en' ? `Upload failed: ${error.message}` : `فشل التحميل: ${error.message}`);
                }}
              />
            </div>
          </div>
        )}

        {/* Violated Worker Camera Modal */}
        {showViolatedWorkerCamera && (
          <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
            <div className="bg-black rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto">
              <div className="flex justify-end p-2">
                <Button
                  variant="ghost"
                  onClick={() => setShowViolatedWorkerCamera(false)}
                  className="rounded-full p-2 bg-white"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
              <InspectionCamera
                userName={auth.user?.profile.name || 'Inspector'}
                inspectionId={taskDetails?.id?.toString() || 'unknown'}
                backendUrl="/sdp/api/v1/document/upload_document"
                establishmentId={updatedTaskDetails?.establishment_id || ''}
                requestId={taskDetails?.id?.toString() || 'unknown'}
                documentCategory="establishment"
                documentType="emirates_id"
                notes="Violated worker Emirates ID"
                ocrRequired="true"
                onUploadSuccess={(responseData) => {
                  console.log("Violated worker document upload response:", responseData);
                  setShowViolatedWorkerCamera(false);
                  toast.success(language === 'en' ? 'Emirates ID uploaded successfully' : 'تم تحميل الهوية الإماراتية بنجاح');

                  // Extract EID from OCR fields if available
                  if (responseData && responseData.data && responseData.data.length > 0 && responseData.data[0].ocr_fields && responseData.data[0].ocr_fields.eid) {
                    // Pre-fill the EID field with the OCR result
                    setEidNumber(responseData.data[0].ocr_fields.eid);
                  } else {
                    // Clear the EID field if no OCR result
                    setEidNumber('');
                  }

                  // Show the EID dialog for user confirmation
                  setShowEidDialog(true);
                }}
                onUploadError={(error) => {
                  setShowViolatedWorkerCamera(false);
                  toast.error(language === 'en' ? `Upload failed: ${error.message}` : `فشل التحميل: ${error.message}`);

                  // Show the EID dialog with empty field
                  setEidNumber('');
                  setShowEidDialog(true);
                }}
              />
            </div>
          </div>
        )}

        {/* EID Confirmation Dialog */}
        {showEidDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-md overflow-hidden shadow-xl">
              <div className="p-4 bg-[#f8f3e9] border-b border-[#e9dcc7]">
                <h3 className="text-lg font-medium text-gray-900">
                  {language === 'en' ? 'Confirm Emirates ID' : 'تأكيد الهوية الإماراتية'}
                </h3>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-4">
                  {language === 'en'
                    ? 'Please enter or confirm the Emirates ID number (15 digits)'
                    : 'الرجاء إدخال أو تأكيد رقم الهوية الإماراتية (15 رقمًا)'}
                </p>
                <input
                  type="text"
                  value={eidNumber}
                  onChange={(e) => setEidNumber(e.target.value)}
                  placeholder="784-XXXX-XXXXXXX-X"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#b89256] focus:border-[#b89256]"
                />
                {eidError && (
                  <p className="text-red-500 text-sm mt-1">{eidError}</p>
                )}
              </div>
              <div className="px-4 py-3 bg-gray-50 flex justify-end space-x-3">
                <button
                  onClick={() => setShowEidDialog(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
                >
                  {language === 'en' ? 'Cancel' : 'إلغاء'}
                </button>
                <button
                  onClick={handleEidSubmit}
                  className="px-4 py-2 bg-[#b89256] text-white rounded-md hover:bg-[#a17d45]"
                >
                  {language === 'en' ? 'Verify Worker' : 'التحقق من العامل'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Worker Violation Modal */}
        {showWorkerViolationModal && workerViolationData && (
          <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-auto">
                <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-3">
                  <div className="container mx-auto px-2">
                    {/* Status Banner */}
                    <div
                      className={`mb-3 rounded-md p-2 flex items-center justify-between ${
                        (taskDetails.establishment_id === workerViolationData.establishment_id) ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {(taskDetails.establishment_id === workerViolationData.establishment_id) ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-red-600" />
                        )}
                        <div>
                          <h2 className={`font-bold text-sm ${(taskDetails.establishment_id === workerViolationData.establishment_id) ? "text-green-700" : "text-red-700"}`}>
                            {(taskDetails.establishment_id === workerViolationData.establishment_id) ? "VALID" : "VIOLATED"}
                          </h2>
                          <p className="text-xs text-muted-foreground">
                            {(taskDetails.establishment_id === workerViolationData.establishment_id)
                              ? "Employee is registered with current establishment"
                              : "Employee is NOT registered with current establishment"}
                          </p>
                        </div>
                      </div>
                      <div className="text-xs text-right">
                        <p className="font-medium">Establishment ID</p>
                        <p className={(taskDetails.establishment_id === workerViolationData.establishment_id) ? "text-green-700" : "text-red-700"}>{workerViolationData.establishment_id}</p>
                      </div>
                    </div>

                    <div className="grid gap-3 lg:grid-cols-[280px_1fr]">
                      {/* Sidebar */}
                      <div className="space-y-3">
                        <Card className="border-none shadow-md overflow-hidden">
                          <div className="h-2 bg-gold-gradient"></div>
                          <CardContent className="p-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-16 w-16 ring-2 ring-gold border-2 border-white">
                                <AvatarFallback className="bg-gold text-white text-lg">
                                  {workerViolationData.full_name_en
                                  .split(" ")
                                  .map((n: any) => n[0])
                                  .join("")
                                  .split("")
                                  .filter((_: any, i: any) => i % 2 === 0)
                                  .join("")}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <h1 className="text-base font-bold leading-tight">{workerViolationData.full_name_en}</h1>
                                <p className="text-xs text-muted-foreground rtl:font-arabic">{workerViolationData.full_name_ar}</p>
                                <div className="flex items-center mt-1 gap-1">
                                  <Flag className="h-3 w-3 text-gold" />
                                  <span className="text-xs">{workerViolationData.country_name_en}</span>
                                </div>
                              </div>
                            </div>

                            <div className="mt-3 flex flex-wrap gap-2">
                              <div className="px-2 py-1 bg-gold rounded-full hover:bg-gold-dark text-white text-xs">
                                {workerViolationData.workpermit_type_name_en}
                              </div>
                              <div className="px-2 py-1 border border-gold rounded-full text-gold text-xs">
                                {calculateAge(workerViolationData.date_of_birth)} years
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card className="border-none shadow-md overflow-hidden">
                          <CardHeader className="bg-gold/10 py-2 px-3">
                            <CardTitle className="text-sm flex items-center gap-1">
                              <IdCard className="h-4 w-4 text-gold" />
                              ID Information
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-3 space-y-2 text-sm">
                            <div className="flex justify-between items-center border-b pb-1">
                              <span className="text-muted-foreground">Person ID</span>
                              <span className="font-medium">{workerViolationData.person_id}</span>
                            </div>
                            <div className="flex justify-between items-center border-b pb-1">
                              <span className="text-muted-foreground">EID Number</span>
                              <span className="font-medium">{workerViolationData.eid_number}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">UID Number</span>
                              <span className="font-medium">{workerViolationData.uid_number}</span>
                            </div>
                          </CardContent>
                        </Card>

                        <Card className="border-none shadow-md overflow-hidden">
                          <CardHeader className="bg-gold/10 py-2 px-3">
                            <CardTitle className="text-sm flex items-center gap-1">
                              <Briefcase className="h-4 w-4 text-gold" />
                              Employment
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-3 space-y-2 text-sm">
                            <div className="flex justify-between items-center border-b pb-1">
                              <span className="text-muted-foreground">Establishment</span>
                              <span className="font-medium text-right max-w-[150px]">{workerViolationData.establishment_name_en}</span>
                            </div>
                            <div className="flex justify-between items-center border-b pb-1">
                              <span className="text-muted-foreground">Establishment ID</span>
                              <span className={`font-medium ${(taskDetails.establishment_id === workerViolationData.establishment_id) ? "text-green-600" : "text-red-600"}`}>
                                {workerViolationData.establishment_id}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Created</span>
                              <span className="font-medium">{formatDate(workerViolationData.created_at)}</span>
                            </div>
                          </CardContent>
                        </Card>

                        <div className="flex justify-end gap-2">
                          <Button
                            onClick={handleSubmitViolationForm}
                            size="sm"
                            className="text-xs bg-gold hover:bg-gold-dark text-white w-1/2"
                            disabled={submittingViolation}
                          >
                            {submittingViolation ? (
                              <>
                                <span className="mr-2">{language === 'en' ? 'Submitting...' : 'جاري التقديم...'}</span>
                                <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              </>
                            ) : (
                              'Submit Violation Form'
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Main Content - Stacked Layout */}
                      <div className="space-y-3">
                        {/* Personal Information Section */}
                        <Card className="border-none shadow-md overflow-hidden">
                          <CardHeader className="bg-gold/10 py-2 px-3 flex flex-row items-center justify-between">
                            <CardTitle className="text-sm flex items-center gap-1">
                              <User className="h-4 w-4 text-gold" />
                              Personal Information
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-3">
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Full Name (English)</p>
                                <p className="font-medium">{workerViolationData.full_name_en}</p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Full Name (Arabic)</p>
                                <p className="font-medium rtl:font-arabic">{workerViolationData.full_name_ar}</p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <div className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3 text-gold" />
                                  <p className="text-xs text-gold font-medium">Date of Birth</p>
                                </div>
                                <p className="font-medium">{formatDate(workerViolationData.date_of_birth)}</p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <div className="flex items-center gap-1">
                                  <User className="h-3 w-3 text-gold" />
                                  <p className="text-xs text-gold font-medium">Gender</p>
                                </div>
                                <p className="font-medium">{getGender(workerViolationData.gender_id)}</p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <div className="flex items-center gap-1">
                                  <Flag className="h-3 w-3 text-gold" />
                                  <p className="text-xs text-gold font-medium">Nationality (English)</p>
                                </div>
                                <p className="font-medium">{workerViolationData.country_name_en}</p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <div className="flex items-center gap-1">
                                  <Flag className="h-3 w-3 text-gold" />
                                  <p className="text-xs text-gold font-medium">Nationality (Arabic)</p>
                                </div>
                                <p className="font-medium rtl:font-arabic">{workerViolationData.country_name_ar}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Employment Details Section */}
                        <Card className="border-none shadow-md overflow-hidden">
                          <CardHeader className="bg-gold/10 py-2 px-3 flex flex-row items-center justify-between">
                            <CardTitle className="text-sm flex items-center gap-1">
                              <Briefcase className="h-4 w-4 text-gold" />
                              Employment Details
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-3">
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Establishment (English)</p>
                                <p className="font-medium">{workerViolationData.establishment_name_en}</p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Establishment (Arabic)</p>
                                <p className="font-medium rtl:font-arabic">{workerViolationData.establishment_name_ar}</p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Establishment ID</p>
                                <p className={`font-medium ${(taskDetails.establishment_id === workerViolationData.establishment_id) ? "text-green-600" : "text-red-600"}`}>
                                  {workerViolationData.establishment_id}
                                </p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Profession</p>
                                <p className="font-medium">{workerViolationData.profession_name_en || "Not specified"}</p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Work Permit Type</p>
                                <p className="font-medium">{workerViolationData.workpermit_type_name_en}</p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Created At</p>
                                <p className="font-medium">{formatDate(workerViolationData.created_at)}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Contract Information Section */}
                        <Card className="border-none shadow-md overflow-hidden">
                          <CardHeader className="bg-gold/10 py-2 px-3 flex flex-row items-center justify-between">
                            <CardTitle className="text-sm flex items-center gap-1">
                              <Calendar className="h-4 w-4 text-gold" />
                              Contract Information
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-3">
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Contract Start</p>
                                <p className="font-medium">
                                  {workerViolationData.contract_start_date
                                    ? formatDate(workerViolationData.contract_start_date)
                                    : "Not specified"}
                                </p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Contract End</p>
                                <p className="font-medium">
                                  {workerViolationData.contract_end_date ? formatDate(workerViolationData.contract_end_date) : "Not specified"}
                                </p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Contract ID</p>
                                <p className="font-medium">{workerViolationData.contract_id || "Not specified"}</p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Salary ID</p>
                                <p className="font-medium">{workerViolationData.salary_id}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Document Information Section */}
                        <Card className="border-none shadow-md overflow-hidden">
                          <CardHeader className="bg-gold/10 py-2 px-3 flex flex-row items-center justify-between">
                            <CardTitle className="text-sm flex items-center gap-1">
                              <IdCard className="h-4 w-4 text-gold" />
                              Document Information
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-3">
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">EID Number</p>
                                <p className="font-medium">{workerViolationData.eid_number}</p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">UID Number</p>
                                <p className="font-medium">{workerViolationData.uid_number}</p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Passport Number</p>
                                <p className="font-medium">{workerViolationData.passport_number || "Not specified"}</p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Person ID</p>
                                <p className="font-medium">{workerViolationData.person_id}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Work Permit Information Section */}
                        <Card className="border-none shadow-md overflow-hidden">
                          <CardHeader className="bg-gold/10 py-2 px-3 flex flex-row items-center justify-between">
                            <CardTitle className="text-sm flex items-center gap-1">
                              <Globe className="h-4 w-4 text-gold" />
                              Work Permit Information
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-3">
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Work Permit Type (EN)</p>
                                <p className="font-medium">{workerViolationData.workpermit_type_name_en}</p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Work Permit Type (AR)</p>
                                <p className="font-medium rtl:font-arabic">{workerViolationData.workpermit_type_name_ar}</p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Work Permit Type ID</p>
                                <p className="font-medium">{workerViolationData.workpermit_type_id}</p>
                              </div>

                              <div className="p-2 rounded-md bg-gold/5 border border-gold/10">
                                <p className="text-xs text-gold font-medium">Work Permit Status</p>
                                <p className="font-medium">{workerViolationData.workpermit_status_name_en || "Not specified"}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        )}
      </div>

      <div className="fixed bottom-[4rem] left-0 right-0 px-4 py-3 border-t border-gray-200 bg-[#f8f3e9] flex items-center justify-between z-10">
        <Button
          onClick={goToPreviousQuestion}
          variant="outline"
          className="px-4 py-2 border border-gray-300 rounded-md flex items-center space-x-1 text-gray-700 hover:bg-gray-100"
        >
          {language === 'ar' ? (
            <>
              <span>{language === 'en' ? 'Previous' : 'السابق'}</span>
              <ArrowRight className="h-4 w-4" />
            </>
          ) : (
            <>
              <ArrowLeft className="h-4 w-4" />
              <span>{language === 'en' ? 'Previous' : 'السابق'}</span>
            </>
          )}
        </Button>

        <div className="text-gray-500">
          {language === 'en'
            ? `Question ${currentQuestionIndex + 1} of ${
                questions.length
              }`
            : `السؤال ${currentQuestionIndex + 1} من ${
                questions.length
              }`}
        </div>

        {/* Next button - disabled if current question is mandatory and not answered */}
        <Button
          onClick={goToNextQuestion}
          disabled={
            currentQuestion?.is_mandatory &&
            !answers[currentQuestion?.id]
          }
          className={`px-4 py-2 rounded-md flex items-center space-x-1 text-white shadow-sm ${
            currentQuestion?.is_mandatory &&
            !answers[currentQuestion?.id]
              ? 'bg-gray-400 cursor-not-allowed opacity-70'
              : 'bg-[#b89256] hover:bg-[#a17d45]'
          }`}
        >
          {submitting ? (
            <>
              <span>
                {language === 'en'
                  ? 'Submitting...'
                  : 'جاري التقديم...'}
              </span>
              <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin ml-1"></div>
            </>
          ) : (
            <>
              {language === 'ar' ? (
                <>
                  <ArrowLeft className="h-4 w-4" />
                  <span>
                    {currentQuestionIndex < questions.length - 1
                      ? language === 'en'
                        ? 'Next'
                        : 'التالي'
                      : language === 'en'
                      ? 'Review Violations'
                      : 'مراجعة'}
                  </span>
                </>
              ) : (
                <>
                  <span>
                    {currentQuestionIndex < questions.length - 1
                      ? language === 'en'
                        ? 'Next'
                        : 'التالي'
                      : language === 'en'
                      ? 'Review Violations'
                      : 'مراجعة'}
                  </span>
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// Success Modal Component
const SuccessModal = ({
  isOpen,
  onClose,
  points,
  language = 'en',
}: {
  isOpen: boolean;
  onClose: () => void;
  points: number;
  language?: string;
}) => {
  if (!isOpen) return null;

  // Import translations from i18n
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-[#00000080]"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-md p-6 z-10 relative animate-fadeIn">
        {/* Animated checkmark */}
        <div className="flex justify-center mb-6">
          <div className="w-24 h-24 rounded-full bg-[#f8f3e9] flex items-center justify-center animate-bounce">
            <CheckCircle className="h-16 w-16 text-[#b89256] animate-pulse" />
          </div>
        </div>

        {/* Success message */}
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">
          {language === 'en'
            ? t('successModal.title')
            : 'تم تقديم تقرير التفتيش بنجاح!'}
        </h2>

        {/* Points earned */}
        <div className="bg-[#f8f3e9] p-4 rounded-lg mb-6">
          <p className="text-center text-gray-700 mb-2">
            {language === 'en'
              ? t('successModal.congratulations')
              : 'تهانينا! لقد حصلت على'}
          </p>
          <p className="text-center text-3xl font-bold text-[#b89256]">
            {points}{' '}
            <span className="text-lg">
              {language === 'en' ? t('successModal.points') : 'نقطة'}
            </span>
          </p>
          <p className="text-center text-gray-700 mt-2">
            {language === 'en'
              ? t('successModal.forCompleting')
              : 'لإكمال هذا التفتيش.'}
          </p>
        </div>

        {/* Continue button */}
        <button
          onClick={onClose}
          className="w-full py-3 bg-[#b89256] text-white rounded-md hover:bg-[#a17d45] transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-[#b89256] focus:ring-opacity-50"
        >
          {language === 'en' ? t('successModal.continue') : 'متابعة'}
        </button>
      </div>
    </div>
  );
};
